import {
    all,
    AllEffect,
    call,
    CallEffect,
    put,
    PutEffect,
    select,
    SelectEffect,
    take,
    TakeEffect,
    takeEvery,
    takeLatest,
} from "redux-saga/effects";

import * as blendingSelectors from "~/action-panel/components/common/product-blend-modal/selectors";
import * as blending from "~/action-panel/components/common/product-blend-modal/blending-utils";

import { setTriggeredPage } from "~/action-panel/components/event-module/actions";
import * as sampleActions from "~/action-panel/components/event-module/components/event-info/components/event-sample-soil/actions";
import * as sampleTissueActions from "~/action-panel/components/event-module/components/event-info/components/event-sample-tissue/actions";
import * as sampleTissueSelectors from "~/action-panel/components/event-module/components/event-info/components/event-sample-tissue/selectors";
import { getUserDepthConfig } from "~/action-panel/components/event-module/components/event-info/components/event-sample-soil/selectors";
import { getColorRamps } from "~/action-panel/components/layer-module/components/layer-list/selectors";
import * as eventListSelectors from "~/action-panel/components/event-module/components/event-list/selectors";
import * as eventListActions from "~/action-panel/components/event-module/components/event-list/actions";
import * as eventInfoActions from "~/action-panel/components/event-module/components/event-info/actions";
import { getCropDropdownState } from "~/action-panel/components/event-module/components/event-info/selectors";
import { picklistNames, selectors as picklistSelectors } from "~/core/picklist";
import { actions as cdActions, selectors as cdSelectors } from "~/customer-data";
import * as loginActions from "~/login/actions";
import { getTheUserGuid } from "~/login/selectors";
import { mapToolsActions } from "~/map";
import {
    setFieldsBackgroundOnly,
    setFieldsBackgroundOnlyBatch,
} from "~/map/components/map-control/actions";
import { actions as notificationActions } from "~/notifications";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import {
    AgEventAPI,
    APIErrorWithCode,
    ClassBreak,
    EventAPI,
    FileImportAPI,
    GeometryMath,
    GeometryUtils,
    InterpolationType,
    LayerAPI,
    LayerUtilsAPI,
    PicklistAPI,
    Toolset,
    buildUnionZones,
} from "@ai360/core";

import * as commonModels from "../model";
import * as recsEventsActions from "../actions";
import { getZonesState } from "../selectors";
import { getAreaListFromSurface, getFieldBoundaryPolygons } from "../sagas";

import { MergeableEvents, MergeableEventType, MergeableSoilEvents } from "~/data/mergeable-events";

import * as models from "./model";
import * as actions from "./actions";
import * as selectors from "./selectors";
import { getIsCopyEventAction } from "./selectors";
import { MSGTYPE } from "~/notifications/actions";
import { messages } from "~/recs-events";
import { v4 as uuid } from "uuid";
import Immutable from "immutable";
import { logFirebaseEvent } from "~/utils/firebase";
import { StatusCodes } from "~/action-panel/components/common/status-messages";
import { ProductMix } from "~/action-panel/components/common/product-blend-modal/model";
import { FieldInfo } from "~/customer-data/models";
import {
    getPickListCode,
    PICKLIST_CROPPING_SEASON,
    PICKLIST_CROP_PURPOSE,
} from "~/core/picklist/picklist-names";
import { showErrors } from "~/action-panel/components/event-module/components/event-list/actions";

type batchGuidMap = {
    count: number;
    newGuid: string;
};

const multiDepthIds = {
    1: "A",
    2: "B",
    3: "C",
    4: "D",
    5: "E",
    6: "F",
    7: "G",
    8: "H",
};

const getBatchGuidMap = (
    eventDetailsList: AgEventAPI.IEventDetails[]
): Map<string, batchGuidMap> => {
    const oldBatchGuidToNew = new Map();
    for (const details of eventDetailsList) {
        let eventChecked = false;
        details.eventAreaList.forEach((area) => {
            const originalBatchGuid = area.agEventList[0].agEventModel.batchSamplingEventGuid;
            if (!originalBatchGuid || eventChecked) {
                //only check each event once, to avoid multiple zones logging to the count property
                return;
            }
            const matchingGuid = oldBatchGuidToNew.get(originalBatchGuid);
            if (!matchingGuid) {
                oldBatchGuidToNew.set(originalBatchGuid, {
                    count: 1,
                    newGuid: uuid(),
                });
            } else {
                const match = oldBatchGuidToNew.get(originalBatchGuid);
                const newValue = {
                    count: (match.count += 1),
                    newGuid: match.newGuid,
                };
                oldBatchGuidToNew.set(originalBatchGuid, newValue);
            }
            eventChecked = true;
        });
    }
    return oldBatchGuidToNew;
};

const getBatchSamplingInfo = (
    agEvent: models.AgEvent,
    oldBatchGuidToNew: Map<string, batchGuidMap>
): models.AgEvent => {
    const hasBatchGuid = agEvent.agEventModel.batchSamplingEventGuid;
    const revisedProps: Partial<models.IAgEventModelMethods> = {
        agEventGuid: "",
    };
    if (hasBatchGuid) {
        const match = oldBatchGuidToNew.get(agEvent.agEventModel.batchSamplingEventGuid);
        const matchingGuid = match?.count > 1 ? match.newGuid : "";
        //if a match count is one or less, then the batchSamplingEventGuid should be cleared
        agEvent = models.AgEvent.prepareSamplingEvent(agEvent, matchingGuid);
        revisedProps.batchSamplingEventGuid = matchingGuid;
    } else {
        agEvent = models.AgEvent.prepareSamplingEvent(agEvent);
    }
    const newAgEventModel = agEvent.agEventModel.updateAgEventModel(revisedProps);
    agEvent = models.AgEvent.updateAgEvent(agEvent, {
        agEventGuid: "",
        agEventModel: newAgEventModel,
    });

    return agEvent;
};

const getAgEventSummaryListFromEventDetails = (
    eventDetailsList: models.EventDetails[],
    picklistOptionsCroppingSeason: PicklistAPI.IPicklistItem[],
    cropData: { guid: string; name: string }[] = null,
    picklistOptionsCropPurpose: { value: string | number; label: string }[] = null,
    fieldMap: Immutable.Map<string, FieldInfo> = null
) => {
    const newEventSummaryItems: models.AgEventSummary[] = eventDetailsList.map((e) => {
        const croppingSeasonName =
            picklistOptionsCroppingSeason.find((o) => o.value === e.croppingSeasonGuid)?.label ??
            "";
        const summary = models.AgEventSummary.fromEventDetails(
            e,
            e.fieldBoundaryGuid,
            null,
            null,
            null,
            null,
            croppingSeasonName,
            9,
            0,
            false,
            null,
            cropData,
            picklistOptionsCropPurpose
        );
        const fieldInfo = fieldMap?.get(e.fieldGuid);
        return fieldInfo != null
            ? summary.updateProps({
                  customerEnrolled: fieldInfo.customerEnrolled,
                  fieldName: fieldInfo.name,
                  farmName: fieldInfo.farmName,
                  fieldAcres: fieldInfo.acres,
                  customerName: fieldInfo.customerName,
              })
            : summary;
    });
    return newEventSummaryItems;
};

const getSampleAgEventFromEventDetails = (eventDetails) => {
    console.assert(eventDetails.isSamplingEvent);
    console.assert(eventDetails.agEventTypeList.length === 1);
    const agEventTypeInfo = eventDetails.agEventTypeList[0];
    console.assert(eventDetails.eventAreaList.length > 0);
    const area = eventDetails.eventAreaList[0];
    console.assert(area.agEventList.length > 0);
    const { agEventModel } = area.agEventList[0];
    console.assert(agEventModel.sampleTypeGuid === agEventTypeInfo.sampleTypeGuid);

    return agEventModel;
};

const getScoutingPhotoGuids = (eventDetails) => {
    const guids = [];
    eventDetails.eventAreaList.forEach((area) => {
        area.agEventList.forEach((agEvent) => {
            if (agEvent.agEventModel.scoutingDetailList) {
                agEvent.agEventModel.scoutingDetailList.forEach((scoutingDetail) => {
                    scoutingDetail.photoList.forEach((photo) => {
                        guids.push(photo.eventScoutingDetailPhotoGuid);
                    });
                });
            }
        });
    });
    return guids;
};

const getScoutingCropPhotoGuids = (eventDetails) => {
    const guids = [];
    eventDetails.eventAreaList.forEach((area) => {
        area.agEventList.forEach((agEvent) => {
            if (agEvent.agEventModel.sitePhotoList) {
                agEvent.agEventModel.sitePhotoList.forEach((photo) => {
                    guids.push(photo.eventScoutingPhotoGuid);
                });
            }
        });
    });
    return guids;
};

const hasScoutingEvent = (eventDetailsList) => {
    return eventDetailsList.find((e) => e.isScoutingEvent) != null;
};

const filterScoutingEvent = (eventDetailsList) => {
    const revisedEventDetailsList = [];
    for (const eventDetail of eventDetailsList) {
        const revisedEventAreaList = eventDetail.eventAreaList
            .filter((area) => area.applyEventToArea)
            .map((a, i) => {
                const agEventList = a.agEventList.map((agEvent) => {
                    const scoutingDetailList = agEvent.agEventModel.scoutingDetailList.filter(
                        (sd) => {
                            return (
                                sd.growthStageGuid ||
                                sd.densityRatingGuid ||
                                sd.observationCount != null ||
                                sd.plantLocationGuid ||
                                sd.weedHeight != null ||
                                sd.trapID ||
                                (sd.photoList && sd.photoList.length > 0)
                            );
                        }
                    );
                    return {
                        ...agEvent,
                        agEventModel: {
                            ...agEvent.agEventModel,
                            scoutingDetailList,
                        },
                    };
                });
                return { ...a, agEventList, eventAreaId: i + 1 };
            });

        const revisedEventDetail = {
            ...eventDetail,
            eventAreaList: revisedEventAreaList,
        };
        revisedEventDetailsList.push(revisedEventDetail);
    }
    return revisedEventDetailsList;
};

const hasInvalidZoneSamplingEvent = (eventDetailsList: models.EventDetails[]): boolean =>
    eventDetailsList
        .filter((e) => e.isSamplingEvent)
        .some(
            (eventDetails) =>
                eventDetails.eventAreaList[0].agEventList[0].agEventModel.interpolationTypeId ===
                    InterpolationType.ZONE_SAMPLING && !eventDetails.hasOneSampleIdPerZone
        );

export const fetchEventTypeInfo = function* (
    action: ReturnType<typeof loginActions.setUserInfo>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const userGuid = yield select(getTheUserGuid);
    try {
        const allTypesResponse = yield call(AgEventAPI.getAgEventTypeList, userGuid);
        const eventTypeInfoList = (allTypesResponse as any).map((jsonObj) =>
            models.AgEventTypeInfo.fromGetAgEventTypeResponseObj(jsonObj)
        );
        yield put(actions.setEventTypeInfo(eventTypeInfoList));
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
    }
};

export const getEventAreaListFromSurface = function* (
    action: ReturnType<typeof recsEventsActions.restoreAreaList>
): Generator<CallEffect | SelectEffect | PutEffect<any>, Readonly<models.AgEventArea>[], any> {
    const classbreakInfo = yield* getAreaListFromSurface(action);
    if (classbreakInfo == null) {
        return;
    }
    const [classIdToClassBreakMap, classIdToGeometryMap] = classbreakInfo;
    const filteredClassBreakMap = [...classIdToClassBreakMap].filter(
        ([classId, value]) => classIdToGeometryMap.has(classId) && value.acreage > 0
    );
    return filteredClassBreakMap.map(([classId, eventAreaClassBreak], idx) => {
        const geometry = classIdToGeometryMap.get(classId);
        console.assert(geometry != null);
        const polygons = GeometryMath.getSimplifiedPolygonParts(geometry).map(
            (poly) => new models.AgEventAreaPolygon(poly)
        );
        const calculatedArea = GeometryUtils.calculateArea(geometry);
        const area = new models.AgEventArea(polygons, [], calculatedArea);
        return models.AgEventArea.updateAgEventArea(area, {
            eventAreaId: idx + 1,
            eventAreaClassBreak,
        });
    });
};

export const onAddAggregateEvent = function* (
    action: ReturnType<typeof actions.addAggregateEvent>
): Generator<SelectEffect | PutEffect<any>, void, any> {
    const { agEventTransactionTypeGuid, sampleTypeGuid } = action.payload;
    const { agEventTypeInfoList, fieldGuidToEventDetails } = yield select(selectors.getModuleState);

    const agEventTypeInfo = agEventTypeInfoList.find(
        (typeInfo) =>
            typeInfo.agEventTransactionTypeGuid === agEventTransactionTypeGuid &&
            typeInfo.sampleTypeGuid === sampleTypeGuid
    );
    console.assert(agEventTypeInfo != null);
    yield put(mapToolsActions.setActiveType(agEventTypeInfo.agEventTransactionTypeName));

    if (agEventTypeInfo.sampleType != null) {
        const fieldGuid = fieldGuidToEventDetails.keys().next().value;
        yield put(recsEventsActions.setSamplingEventId(fieldGuid));
    }
};

export const onApplyYieldCalibration = function* (
    action: ReturnType<typeof actions.applyYieldCalibration>
): Generator<
    | CallEffect
    | SelectEffect
    | PutEffect<{
          type: string;
          payload: {
              err: any;
              retryAction: any;
              intlMessageObj: any;
              intlMessageValues: any;
          };
      }>,
    void,
    unknown
> {
    const { yieldCalibrationModel } = action.payload;
    const userGuid = yield select(getTheUserGuid);
    try {
        yield call(AgEventAPI.calibrateYield, userGuid, yieldCalibrationModel);
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
    }
};

export const onBatchSamplingReset = function* (): Generator<PutEffect<any>, void, any> {
    yield put(mapToolsActions.setBatchReset(true));
    yield put(actions.setSamplingFieldPointsPlaced(false));
};

export const onCopyBatchTemplateToEvents = function* (
    action: ReturnType<typeof actions.copyBatchTemplateToEvents>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    yield put(eventInfoActions.setEventDetailsLoading(true));
    const userGuid = yield select(getTheUserGuid);
    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);

    const eventDetailsList = [...fieldGuidToEventDetails.values()]
        .filter((eventDetails) => eventDetails.fieldGuid !== commonModels.BATCH_TEMPLATE_FIELD_GUID)
        .map((eventDetails) =>
            models.EventDetails.updateEventDetails(eventDetails, {
                startDate: `${eventDetails.momentStartDate.format("YYYY-MM-DD")}T20:00:00.000Z`,
                startTime: eventDetails.momentStartDate.format("h:mma"),
                endDate: `${eventDetails.momentEndDate.format("YYYY-MM-DD")}T20:00:00.000Z`,
                endTime: eventDetails.momentEndDate.format("h:mma"),
            })
        );

    try {
        yield call(AgEventAPI.validEventGeneral, userGuid, eventDetailsList);
    } catch (err) {
        if (!(err instanceof APIErrorWithCode)) {
            yield put(notificationActions.apiCallError(err, action));
        } else {
            yield put(actions.saveEventDetailsFailed(err.errorCodeList, err.model));
        }
        yield put(eventInfoActions.setEventDetailsLoading(false));
        return;
    }

    yield put(recsEventsActions.setCurrentBatchFieldGuid(null));
    yield put(recsEventsActions.initializeEventsZones());
};

function getScoutingS3PhotoCopyMap(eventDetails) {
    const currentPhotoGuidToNewGuidMap = new Map();
    eventDetails.eventAreaList.forEach((area) => {
        area.agEventList.forEach((agEvent) => {
            if (eventDetails.isScoutingEvent && agEvent.agEventModel.scoutingDetailList) {
                agEvent.agEventModel.scoutingDetailList.forEach((scoutingDetail) => {
                    scoutingDetail.photoList.forEach((photo) => {
                        currentPhotoGuidToNewGuidMap.set(
                            photo.eventScoutingDetailPhotoGuid,
                            uuid()
                        );
                    });
                });
            }
        });
    });
    return currentPhotoGuidToNewGuidMap;
}

function* copyS3Photos(copyPhotoMap) {
    const userGuid = yield select(getTheUserGuid);
    const copyPhotoList = [];
    for (const [key, value] of copyPhotoMap) {
        copyPhotoList.push({
            existingPhotoGuid: key,
            newPhotoGuid: value,
        });
    }
    yield call(AgEventAPI.copyScoutingPhotos, userGuid, copyPhotoList);
    yield put(
        actions.cacheTemporaryScoutingPhotoList(
            copyPhotoList.map((p) => {
                return `${p.newPhotoGuid.toUpperCase()}.png`;
            })
        )
    );
}

function* getCopiedEventAreaList(
    eventDetails,
    areaIdToNewAreaIdPolygonMap,
    currentPhotoGuidToNewGuidMap = new Map()
) {
    const samplingEventIdRequest: AgEventAPI.ISamplingEventIdRequest = {
        blockSize: 1,
        deviceVendorId: null,
        isThirdParty: false,
    };
    const eventId = yield call(AgEventAPI.getNextEventId, samplingEventIdRequest);
    const isCopyAction = yield select(getIsCopyEventAction);
    const newPolygonsIds = Array.from(areaIdToNewAreaIdPolygonMap.keys());

    return eventDetails.eventAreaList
        .filter((x) => {
            if (isCopyAction) {
                return newPolygonsIds.includes(x.eventAreaId) ? x : null;
            }
            return x;
        })
        .filter(Boolean)
        .map((area) => {
            const newAgEventList = area.agEventList.map((agEvent) => {
                const newAgEventModel = agEvent.agEventModel.updateAgEventModel({
                    eventId,
                    agEventGuid: "",
                    productMixList: agEvent.agEventModel.productMixList?.map((x) => ({
                        ...x,
                        productMixGuid: "",
                        products: x.products.map((x) => ({
                            ...x,
                            productMixProductGuid: "",
                        })),
                        nutrients: x.nutrients.map((x) => ({
                            ...x,
                            productMixNutrientGuid: "",
                        })),
                    })),
                });

                agEvent = models.AgEvent.updateAgEvent(agEvent, {
                    agEventGuid: "",
                    agEventModel: newAgEventModel,
                });
                if (agEvent.agEventModel instanceof models.SampleSetup) {
                    agEvent = models.AgEvent.prepareSamplingEvent(agEvent);
                }
                if (eventDetails.isScoutingEvent && agEvent.agEventModel.scoutingDetailList) {
                    agEvent.agEventModel.scoutingDetailList.forEach((scoutingDetail) => {
                        scoutingDetail.eventScoutingDetailGuid = "";
                        scoutingDetail.photoList.forEach(function (photo) {
                            photo.eventScoutingDetailPhotoGuid = currentPhotoGuidToNewGuidMap.get(
                                photo.eventScoutingDetailPhotoGuid
                            );
                        });
                    });
                }
                return agEvent;
            });
            const { fieldBoundaryGuid, shape } = areaIdToNewAreaIdPolygonMap
                .get(area.eventAreaId)
                .get(area.eventAreaId);
            const zonePolygons = GeometryMath.getSimplifiedPolygonParts(shape).map(
                (poly) => new models.AgEventAreaPolygon(poly)
            );
            return models.AgEventArea.updateAgEventArea(area, {
                zonePolygons,
                eventAreaGuid: "",
                fieldBoundaryGuid,
                agEventGeneralGuid: "",
                agEventList: newAgEventList,
            });
        });
}

function* getDefaultCroppingSeason(eventDetails) {
    try {
        const picklistOptionsCroppingSeason = yield select(
            picklistSelectors.getPicklistOptionsFromCode,
            picklistNames.getPickListCode(picklistNames.PICKLIST_CROPPING_SEASON)
        );
        const curYearStr = String(new Date().getFullYear());
        const defaultOption = picklistOptionsCroppingSeason.find(
            (option) => option.label === curYearStr
        );
        return defaultOption.value;
    } catch (e) {
        return eventDetails.croppingSeasonGuid;
    }
}

export const onBatchCopyEvents = function* (
    action: ReturnType<typeof actions.batchCopyEvents>
): Generator<PutEffect<any>, void, any> {
    const { selectedEvents, fieldGuidList, fieldGuidToBoundaryGuidMap } = action.payload;
    const agEventGeneralGuidList = selectedEvents.map((e) => e.agEventGeneralGuid);
    const agEventTransactionTypeName = selectedEvents[0].agEventTypeName;

    yield put(
        actions.batchCopyEventDetails(
            agEventGeneralGuidList,
            agEventTransactionTypeName,
            fieldGuidList,
            fieldGuidToBoundaryGuidMap
        )
    );
};

export const onBatchCopyEventDetails = function* (
    action: ReturnType<typeof actions.batchCopyEventDetails>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const {
        agEventGeneralGuidList,
        agEventTransactionTypeName,
        fieldGuidList,
        fieldGuidToBoundaryGuidMap,
    } = action.payload;
    const userGuid = yield select(getTheUserGuid);
    yield put(eventInfoActions.setEventDetailsLoading(true));

    try {
        const batchEventDetails = yield call(
            AgEventAPI.getBatchAgEvents,
            userGuid,
            agEventGeneralGuidList
        );
        const allEventDetails: models.EventDetails[] = [];
        const oldBatchGuidToNew = getBatchGuidMap(batchEventDetails);

        for (const details of batchEventDetails) {
            const eventDetails = models.EventDetails.fromJsonObj(details);
            const newEventAreaList = eventDetails.eventAreaList.map((area) => {
                const newAgEventList = area.agEventList.map((agEvent) => {
                    if (agEvent.agEventModel instanceof models.SampleSetup) {
                        agEvent = getBatchSamplingInfo(agEvent, oldBatchGuidToNew);
                    } else {
                        const newAgEventModel = agEvent.agEventModel.updateAgEventModel({
                            agEventGuid: "",
                        });
                        agEvent = models.AgEvent.updateAgEvent(agEvent, {
                            agEventGuid: "",
                            agEventModel: newAgEventModel,
                        });
                    }
                    return agEvent;
                });
                return models.AgEventArea.updateAgEventArea(area, {
                    agEventGeneralGuid: "",
                    agEventList: newAgEventList,
                    eventAreaGuid: "",
                    sourceEventAreaGuid: area.eventAreaGuid,
                    zonePolygons: null,
                });
            });
            const updatedEventDetails = models.EventDetails.updateEventDetails(eventDetails, {
                agEventGeneralGuid: "",
                eventAreaList: newEventAreaList,
            });
            allEventDetails.push(updatedEventDetails);
        }

        const fieldBoundaryGuid = fieldGuidToBoundaryGuidMap.get(
            commonModels.BATCH_TEMPLATE_FIELD_GUID
        );
        const batchDetail = Object.freeze(
            new models.EventDetails(
                commonModels.BATCH_TEMPLATE_FIELD_GUID,
                fieldBoundaryGuid,
                allEventDetails[0].agEventTypeList
            )
        );

        allEventDetails.splice(0, 0, batchDetail);

        yield put(actions.fetchBatchEventDetailsSucceeded(allEventDetails));
        yield put(
            actions.createNewCopyBatchEvent(
                fieldGuidList,
                fieldGuidToBoundaryGuidMap,
                agEventTransactionTypeName
            )
        );
        yield put(actions.setBatchCopySamplePoints());
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
    }
    yield put(eventInfoActions.setEventDetailsLoading(false));
};

export const onCopyEventDetails = function* (
    action: ReturnType<typeof actions.copyEventDetails>
): Generator<
    | Generator<CallEffect | SelectEffect | PutEffect<any>, void, unknown>
    | CallEffect
    | SelectEffect
    | PutEffect<any>
    | TakeEffect,
    void,
    any
> {
    const fetchSamplingResults = false;
    const isCopyAction = true;
    yield put(
        actions.fetchEventDetails(
            action.payload.agEventGeneralGuid,
            fetchSamplingResults,
            isCopyAction
        )
    );
    const fetchSuccessfulAction = yield take(actions.FETCH_EVENT_DETAILS_SUCCEEDED);
    const { eventDetails } = fetchSuccessfulAction.payload;
    if (eventDetails.agEventGeneralGuid !== action.payload.agEventGeneralGuid) {
        return;
    }

    const updateAreaAction = yield take(recsEventsActions.UPDATE_EVENT_AREA_POLYGONS);
    const { areaIdToNewAreaIdPolygonMap } = updateAreaAction.payload;

    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);
    console.assert(fieldGuidToEventDetails.size === 1);

    // Reset `agEventGeneralGuid` and update the dates to the default for new events
    const fieldGuid = fieldGuidToEventDetails.keys().next().value;

    const copyPhotoMap = eventDetails.isScoutingEvent
        ? getScoutingS3PhotoCopyMap(eventDetails)
        : new Map();

    if (copyPhotoMap.size > 0) {
        yield copyS3Photos(copyPhotoMap);
    }

    const newEventAreaList = yield getCopiedEventAreaList(
        eventDetails,
        areaIdToNewAreaIdPolygonMap,
        copyPhotoMap
    );
    const croppingSeasonGuid = yield getDefaultCroppingSeason(eventDetails);
    const { agEventGeneralGuid, momentStartDate, momentEndDate } = new models.EventDetails(
        "",
        "",
        []
    );
    yield put(
        actions.updateEventDetails(fieldGuid, {
            agEventGeneralGuid,
            croppingSeasonGuid,
            momentStartDate,
            momentEndDate,
            eventAreaList: newEventAreaList,
        })
    );
    yield put(actions.setEventIsCopyAction(false));
    yield put(actions.setEventBoundaryGraphic(null));
    yield put(eventInfoActions.setEventDetailsLoading(false));
};

export const onCreateNewBatchEvent = function* (
    action: ReturnType<typeof actions.createNewBatchEvent>
): Generator<PutEffect<any>, void, any> {
    const {
        fieldGuidList,
        fieldGuidToBoundaryGuidMap,
        fieldGuidToCustomerGuidMap,
        agEventTransactionTypeName,
    } = action.payload;
    yield put(setFieldsBackgroundOnlyBatch(true));
    fieldGuidList.splice(0, 0, commonModels.BATCH_TEMPLATE_FIELD_GUID);
    yield put(
        actions.createNewEventDetails(
            fieldGuidList,
            fieldGuidToBoundaryGuidMap,
            fieldGuidToCustomerGuidMap,
            agEventTransactionTypeName
        )
    );
};

export const onCreateNewCopyBatchEvent = function* (
    action: ReturnType<typeof actions.createNewCopyBatchEvent>
): Generator<SelectEffect | PutEffect<any>, void, any> {
    const { fieldGuidList, fieldGuidToBoundaryGuidMap, agEventTransactionTypeName } =
        action.payload;
    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);
    yield put(setFieldsBackgroundOnly(true));

    if (fieldGuidToEventDetails.entries().next().value[1].isSamplingEvent) {
        for (const fieldGuid of fieldGuidToEventDetails.keys()) {
            yield put(recsEventsActions.setSamplingEventId(fieldGuid));
        }
    } else {
        for (const [fieldGuid, eventDetails] of fieldGuidToEventDetails.entries()) {
            console.assert(eventDetails.eventAreaList.length >= 1);
            const { eventAreaId } = eventDetails.eventAreaList[0];
            yield put(recsEventsActions.setCurrentAreaId(fieldGuid, eventAreaId));
        }
    }
    fieldGuidList.splice(0, 0, commonModels.BATCH_TEMPLATE_FIELD_GUID);

    yield put(
        actions.createCopyBatchEventDetails(
            fieldGuidList,
            fieldGuidToBoundaryGuidMap,
            agEventTransactionTypeName
        )
    );
};

export const onCreateNewClassifiedEvent = function* (
    action:
        | ReturnType<typeof actions.createNewClassifiedEvent>
        | ReturnType<typeof recsEventsActions.restoreAreaList>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const { field, agEventTransactionTypeName, triggeredPage } = (
        action as ReturnType<typeof actions.createNewClassifiedEvent>
    ).payload;

    if (triggeredPage) {
        yield put(setTriggeredPage(triggeredPage));
    }

    const eventAreaList = yield* getEventAreaListFromSurface(
        action as ReturnType<typeof recsEventsActions.restoreAreaList>
    );
    yield put(
        actions.createNewEventDetails(
            [field.fieldGuid],
            new Map([[field.fieldGuid, field.fieldBoundaryGuid]]),
            new Map([[field.fieldGuid, field.customerGuid]]),
            agEventTransactionTypeName,
            eventAreaList
        )
    );
};

export const onCreateNewEventDetails = function* (): Generator<
    SelectEffect | PutEffect<any>,
    void,
    any
> {
    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);
    yield put(setFieldsBackgroundOnly(true));
    for (const [fieldGuid, eventDetails] of fieldGuidToEventDetails.entries()) {
        console.assert(eventDetails.eventAreaList.length >= 1);
        if (eventDetails.isSamplingEvent) {
            yield put(recsEventsActions.setSamplingEventId(fieldGuid));
        } else {
            const { eventAreaId } = eventDetails.eventAreaList[0];
            yield put(recsEventsActions.setCurrentAreaId(fieldGuid, eventAreaId));
        }
    }
};

export const onDeleteEvents = function* (
    action: ReturnType<typeof actions.deleteEvents>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const { agEventGeneralGuidList } = action.payload;
    const userGuid = yield select(getTheUserGuid);
    try {
        yield call(AgEventAPI.deactivateEvents, userGuid, agEventGeneralGuidList);
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
        return;
    }
    yield put(actions.deleteEventsSuccessful());
};

export const onActivateEvent = function* (
    action: ReturnType<typeof actions.activateEvent>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const { agEventGeneralGuid } = action.payload;
    const userGuid = yield select(getTheUserGuid);

    try {
        yield call(AgEventAPI.activateEvent, userGuid, agEventGeneralGuid);
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
        return;
    }
    yield put(actions.activateEventSuccessful());
};

export const onFetchEventDetails = function* (
    action: ReturnType<typeof actions.fetchEventDetails>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const { agEventGeneralGuid, fetchSamplingResults, isCopyAction } = action.payload;
    const userGuid = yield select(getTheUserGuid);
    try {
        const response = yield call(AgEventAPI.getAgEvents, userGuid, agEventGeneralGuid);
        const eventDetails = models.EventDetails.fromJsonObj(response);

        if (isCopyAction) {
            const newEventAreaList = eventDetails.eventAreaList.map((area) => {
                const newAgEventList = area.agEventList.map((agEvent) => {
                    const newAgEventModel = agEvent.agEventModel.updateAgEventModel({
                        batchSamplingEventGuid: "",
                    });
                    if (agEvent.agEventModel instanceof models.SampleSetup) {
                        agEvent = models.AgEvent.prepareSamplingEvent(agEvent);
                    }
                    return models.AgEvent.updateAgEvent(agEvent, {
                        agEventGuid: "",
                        agEventModel: newAgEventModel,
                    });
                });
                return models.AgEventArea.updateAgEventArea(area, {
                    agEventList: newAgEventList,
                });
            });

            const updatedEventDetails = models.EventDetails.updateEventDetails(eventDetails, {
                eventAreaList: newEventAreaList,
            });

            yield put(
                actions.fetchEventDetailsSucceeded(
                    updatedEventDetails,
                    fetchSamplingResults,
                    isCopyAction
                )
            );
        } else {
            yield put(actions.fetchEventDetailsSucceeded(eventDetails, fetchSamplingResults));
        }
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
    }
};

export const onFetchEventDetailsSucceeded = function* (
    action: ReturnType<typeof actions.fetchEventDetailsSucceeded>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const { eventDetails, fetchSamplingResults } = action.payload;
    const eventAreaId = Math.min(
        ...eventDetails.eventAreaList.map((agEventArea) => agEventArea.eventAreaId)
    );
    yield put(recsEventsActions.setCurrentAreaId(eventDetails.fieldGuid, eventAreaId));

    const { mergeableEvents } = yield select(eventListSelectors.getModuleState);
    if (
        mergeableEvents &&
        mergeableEvents.type === MergeableEventType.Soil &&
        mergeableEvents.hasSoilSampleResults
    ) {
        yield put(recsEventsActions.fetchMergeSoilSampleResults(mergeableEvents));
        return;
    }
    const { fieldGuidToEventListMap } = yield select(selectors.getModuleState);
    const eventSummary = fieldGuidToEventListMap
        .get(eventDetails.fieldGuid)
        .find(
            (agEventSummary) =>
                agEventSummary.agEventGeneralGuid === eventDetails.agEventGeneralGuid
        );
    if (eventSummary != null && eventSummary.importedPoints > 0 && fetchSamplingResults) {
        const agEventModel = getSampleAgEventFromEventDetails(eventDetails);
        const isTissue = eventDetails.agEventTypeList.some(
            (agEventType) =>
                agEventType?.agEventTransactionTypeName === models.EVENT_TYPE_NAME_SAMPLING_TISSUE
        );
        if (isTissue) {
            yield put(recsEventsActions.fetchTissueSampleResults(agEventModel.agEventGuid));
        } else {
            yield put(recsEventsActions.fetchSoilSampleResults(agEventModel.agEventGuid));
        }
    }
    if (eventDetails.isSamplingEvent && eventSummary != null) {
        const agEventModel = getSampleAgEventFromEventDetails(eventDetails);
        const setProductivityAction = eventDetails.agEventTypeList.some(
            (agEventType) => agEventType.typeName === models.EVENT_TYPE_NAME_SAMPLING_TISSUE
        )
            ? sampleTissueActions.setShowProductivityRating
            : sampleActions.setShowProductivityRating;
        if (
            agEventModel.samplePoints.some(
                (point) =>
                    point.productivityRatingGuid != null && point.productivityRatingGuid !== ""
            )
        ) {
            yield put(setProductivityAction(true));
        } else {
            yield put(setProductivityAction(false));
        }
    }

    if (eventDetails.isScoutingEvent) {
        // Generate presigned urls for any Scouting photos and preload
        const scoutingPhotoGuids = getScoutingPhotoGuids(eventDetails);
        const scoutingCropPhotoGuids = getScoutingCropPhotoGuids(eventDetails);
        if (scoutingPhotoGuids.length > 0 || scoutingCropPhotoGuids.length > 0) {
            try {
                const photoGuids = scoutingPhotoGuids.concat(scoutingCropPhotoGuids);
                const result = yield call(
                    AgEventAPI.generateScoutingPhotoS3PreSignedUrls,
                    photoGuids
                );
                const presignedUrlMap = new Map(Object.entries(result));
                yield put(
                    recsEventsActions.setScoutingPhotoPresignedUrlMap(
                        presignedUrlMap as Map<string, string>
                    )
                );
                // Preload the images
                for (const url of presignedUrlMap.values()) {
                    new Image().src = url as string;
                }
            } catch {
                yield put(
                    notificationActions.pushToasterMessage(
                        messages.s3PhotoGeneratePresignedUrlsFailed,
                        MSGTYPE.ERROR
                    )
                );
            }
        }
    }
};

export const onFetchLayerNameToSurfaceInfoMap = function* (
    action: ReturnType<typeof actions.fetchLayerNameToSurfaceInfoMap>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const { fieldGuid } = action.payload;

    let { layerNameToSurfaceInfoMap } = yield select(selectors.getModuleState);

    const userGuid = yield select(getTheUserGuid);
    const eventTypeNameToGuidMap = yield select(selectors.getEventTypeNameToTransactionGuidMap);

    let layerInfoList;
    try {
        layerInfoList = yield call(LayerAPI.getAvailableLayersByFieldGuid, userGuid, fieldGuid);
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
        return;
    }

    // FIXME: check that it has the same boundary guid
    // Keep Soil Type, EC Data, Yield Data, and Analysis Layers
    const transactionTypeGuids = new Set([
        eventTypeNameToGuidMap.get("EC Data"),
        eventTypeNameToGuidMap.get("Harvest"),
    ]);

    layerNameToSurfaceInfoMap = new Map();
    for (const layerInfo of layerInfoList) {
        const layerType = LayerUtilsAPI.getLayerType(layerInfo);
        if (
            layerType !== LayerUtilsAPI.LayerType.SOIL &&
            layerType !== LayerUtilsAPI.LayerType.ANALYSIS &&
            !(
                layerType === LayerUtilsAPI.LayerType.EVENT_IMPORTED &&
                transactionTypeGuids.has(layerInfo.agEventTransactionTypeGuid)
            )
        ) {
            continue;
        }

        let selectedSurfaceGuid = layerInfo.selectedSurfaceGuid;
        let selectedSurfaceTypeGuid = null;
        if (layerType === LayerUtilsAPI.LayerType.EVENT_IMPORTED) {
            // Need to find the polygon surface for the selected attribute
            const selectedAttributeGuid = layerInfo.selectedAttributeGuid;
            const polygonSurface = layerInfo.subLayers.find(
                (s) =>
                    s.importAttributeGuid === selectedAttributeGuid &&
                    s.surfaceType === "Event (Imported)"
            );
            if (polygonSurface == null) {
                continue;
            }
            selectedSurfaceGuid = polygonSurface.surfaceGuid;
            selectedSurfaceTypeGuid = polygonSurface.surfaceTypeGuid;
        }

        const surfaceInfo = LayerUtilsAPI.getSurfaceInfo(
            layerInfo,
            selectedSurfaceGuid,
            layerInfo.selectedAttributeGuid,
            selectedSurfaceTypeGuid
        );
        if (surfaceInfo == null) {
            continue;
        }

        const displayName =
            layerType === LayerUtilsAPI.LayerType.ANALYSIS
                ? `${surfaceInfo.displayName} - ${layerInfo.displayName}`
                : layerInfo.displayName;

        layerNameToSurfaceInfoMap.set(displayName, surfaceInfo);
    }

    yield put(actions.setLayerNameToSurfaceInfoMap(layerNameToSurfaceInfoMap));
};

export const onFetchSoilSampleResults = function* (
    action: ReturnType<typeof actions.fetchSoilSampleResults>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const { agEventGuid } = action.payload;
    const userGuid = yield select(getTheUserGuid);
    try {
        const sampleResults = yield call(FileImportAPI.getSoilSampleResults, userGuid, agEventGuid);
        yield put(actions.fetchSoilSampleResultsSucceeded(sampleResults));
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
    }
};

export const onFetchTissueSampleResults = function* (
    action: ReturnType<typeof actions.fetchTissueSampleResults>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const { agEventGuid } = action.payload;
    const userGuid = yield select(getTheUserGuid);
    try {
        const sampleResults = yield call(
            FileImportAPI.getTissueSampleResults,
            userGuid,
            agEventGuid
        );
        yield put(actions.fetchTissueSampleResultsSucceeded(sampleResults as any));
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
    }
};

export const onFetchMergeSoilSampleResults = function* (
    action: ReturnType<typeof actions.fetchMergeSoilSampleResults>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const mergeableEvents = action.payload.mergeableEvents as MergeableEvents;
    const userGuid = yield select(getTheUserGuid);
    try {
        const sampleResults = yield call(
            AgEventAPI.getMergeSoilSampleResults,
            userGuid,
            mergeableEvents.all
        );
        yield put(actions.fetchSoilSampleResultsSucceeded(sampleResults as any));
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
    }
};

export const onFetchYieldCalibration = function* (
    action: ReturnType<typeof actions.yieldCalibrationComplete>
): Generator<AllEffect | CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const { agEventGuidList, windowLoad } = action.payload;
    //TODO: Add check to see if we need to fetch or already have the data in the map
    const userGuid = yield select(getTheUserGuid);
    try {
        const results = yield all([
            yield call(AgEventAPI.getHarvestLoadList, userGuid, {
                agEventGuidList,
                windowLoad,
            }),
            windowLoad ? yield call(AgEventAPI.getYieldCalibrationTypePicklist, userGuid) : null,
        ]);
        yield put(actions.fetchYieldCalibrationCompleted(results));
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
        yield put(actions.fetchYieldCalibrationCompleted(null));
    }
};

export const onRefreshYieldCalibration = function* (
    action: ReturnType<typeof actions.refreshYieldCalibration>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    yield put(actions.fetchYieldCalibration(action.payload[0], false));
};

export const onInitializeEventsZones = function* (
    action: ReturnType<typeof actions.initializeEventsZones>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);

    const eventDetailsList = [...fieldGuidToEventDetails.values()];
    let filteredEventDetailsList = eventDetailsList;

    const isBatchCopy = eventDetailsList.some((eventDetail) =>
        eventDetail.eventAreaList.some(
            (eventArea) =>
                eventArea.sourceEventAreaGuid != null && eventArea.sourceEventAreaGuid !== ""
        )
    );

    if (isBatchCopy) {
        //Skip field boundary assignment to event details that have copied zones
        filteredEventDetailsList = eventDetailsList.filter(
            (eventDetail) => eventDetail.eventAreaList.length === 1
        );
    }

    const fieldGuidList = filteredEventDetailsList
        .map((eventDetails) => {
            console.assert(eventDetails.eventAreaList.length === 1);
            console.assert(eventDetails.eventAreaList[0].zonePolygons == null);
            return eventDetails.fieldGuid;
        })
        .filter((fieldGuid) => fieldGuid !== commonModels.BATCH_TEMPLATE_FIELD_GUID);

    const getAreaIdFromFieldGuid = (fieldGuid) => {
        const { eventAreaId } = fieldGuidToEventDetails.get(fieldGuid).eventAreaList[0];
        return eventAreaId;
    };
    let fieldGuidToAreaUpdateMap;
    try {
        fieldGuidToAreaUpdateMap = yield* getFieldBoundaryPolygons(
            fieldGuidList,
            getAreaIdFromFieldGuid
        );
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
        return;
    }
    const conversionFactors = yield select(blendingSelectors.getConversionFactors);
    const productBlendPicklists = yield select(blendingSelectors.getProductBlendPicklists);

    for (const [fieldGuid, areaIdToNewAreaIdPolygonMap] of fieldGuidToAreaUpdateMap.entries()) {
        yield put(
            actions.updateEventAreaPolygons(
                fieldGuid,
                areaIdToNewAreaIdPolygonMap,
                conversionFactors,
                productBlendPicklists
            )
        );
    }
    yield put(eventInfoActions.setEventDetailsLoading(false));
};

export const onInitializeEventsZonesSucceeded = function* () {
    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);
    const conversionFactors = yield select(blendingSelectors.getConversionFactors);
    const productBlendPicklists = yield select(blendingSelectors.getProductBlendPicklists);

    const eventsToUpdate: models.EventDetails[] = [...fieldGuidToEventDetails.values()]
        .filter((r) => r.fieldGuid !== commonModels.BATCH_TEMPLATE_FIELD_GUID)
        .map((eventDetails) => {
            const newProps: Partial<models.EventDetails> = {};
            newProps.eventAreaList = eventDetails.eventAreaList.map(
                (eventArea: models.AgEventArea) => {
                    return models.AgEventArea.updateAgEventArea(eventArea, {
                        agEventList: eventArea.agEventList.map((event) => {
                            if (event?.agEventModel instanceof models.AgEventApplication) {
                                return models.AgEvent.updateAgEvent(event, {
                                    agEventModel: event.agEventModel.updateAgEventModel({
                                        productMixList:
                                            blending.updateProductMixListForServiceProducts(
                                                event.agEventModel.productMixList,
                                                conversionFactors,
                                                productBlendPicklists,
                                                eventArea.calculatedArea
                                            ) as ProductMix[],
                                    }),
                                });
                            }
                            return event;
                        }),
                    });
                }
            );
            return models.EventDetails.updateEventDetails(eventDetails, newProps);
        });
    const batchEventsForUpdate = eventsToUpdate.map((eventDetails) => {
        return {
            fieldGuid: eventDetails.fieldGuid,
            newProps: { ...eventDetails },
        };
    });
    yield put(recsEventsActions.batchUpdateEventDetails(batchEventsForUpdate));
    yield put(recsEventsActions.clearSaveEventDetailsErrorCodeList());
};

const retrieveDefaultDepthConfig = function* (sampleTypeGuid: string) {
    const retrieved = yield select(getUserDepthConfig);
    if (retrieved) {
        return retrieved;
    }

    yield put(sampleActions.fetchSampleSoilEventDefaults(sampleTypeGuid));
    yield take(sampleActions.SET_USER_DEPTH_CONFIG);
    return yield select(getUserDepthConfig);
};

export const onDeleteTemporaryScoutingPhotos = function* (): Generator<
    CallEffect | SelectEffect | PutEffect<any>,
    void,
    any
> {
    const { temporaryScoutingPhotoList } = yield select(selectors.getModuleState);
    if (temporaryScoutingPhotoList.length > 0) {
        yield call(AgEventAPI.deleteScoutingPhotos, temporaryScoutingPhotoList);
        yield put(recsEventsActions.clearTemporaryScoutingPhotoList());
    }
};

export const buildEventDetailsWithZones = (rawDetailsWithZones): models.EventDetails[] =>
    rawDetailsWithZones.map((details) => {
        const eventDetails = models.EventDetails.fromJsonObj(details);
        const rawAreaByGuid = new Map<string, any>(
            details.eventAreaList.map((a) => [a.eventAreaGuid, a])
        );
        const updatedAreaList = eventDetails.eventAreaList.map((area) => {
            const rawArea = rawAreaByGuid.get(area.eventAreaGuid);
            if (rawArea?.zonePolygons) {
                return models.AgEventArea.updateAgEventArea(area, {
                    zonePolygons: rawArea.zonePolygons.map(
                        (p) => new models.AgEventAreaPolygon(p.shape)
                    ),
                });
            }
            return area;
        });
        return models.EventDetails.updateEventDetails(eventDetails, {
            eventAreaList: updatedAreaList,
        });
    });

export const blankClassBreakHeadingsIfNeeded = (
    areaLists: models.AgEventArea[][]
): ((areas: models.AgEventArea[]) => models.AgEventArea[]) => {
    const headings = areaLists
        .flat()
        .filter((area) => area.eventAreaClassBreak != null)
        .map((area) => area.eventAreaClassBreak.heading);
    const uniqueHeadings = new Set(headings.filter(Boolean));
    if (uniqueHeadings.size <= 1) {
        return (areas) => areas;
    }
    return (areas) =>
        areas.map((area) => {
            if (area.eventAreaClassBreak == null) {
                return area;
            }
            return {
                ...area,
                eventAreaClassBreak: new ClassBreak({ ...area.eventAreaClassBreak, heading: "" }),
            };
        });
};

const getZoneShape = (area: models.AgEventArea) => {
    const shapes = (area.zonePolygons ?? []).map((p) => p.shape).filter(Boolean);
    return shapes.length ? GeometryUtils.getCombinedBoundaries(shapes) : null;
};

const polygonsOverlap = (polyI, polyJ): boolean =>
    geometryEngine.intersects(polyI, polyJ) && !geometryEngine.touches(polyI, polyJ);

const findMaxPointIds = (points: AgEventAPI.ISoilSamplePoint[]) => {
    let nextSampleId = 0;
    let nextSequenceId = 0;
    for (const p of points) {
        nextSampleId = Math.max(nextSampleId, p.sampleId ?? 0);
        nextSequenceId = Math.max(nextSequenceId, p.sequenceId ?? 0);
    }
    return { nextSampleId, nextSequenceId };
};

const renumberSoilPoint = (
    p: AgEventAPI.ISoilSamplePoint,
    agEventGuid: string,
    sampleId: number,
    sequenceId: number,
    preserveGuids = false
): AgEventAPI.ISoilSamplePoint => ({
    ...p,
    agEventGuid,
    sampleId,
    sequenceId,
    soilSamplePointGuid: preserveGuids ? p.soilSamplePointGuid : null,
    samplePointDepths:
        p.samplePointDepths?.map((d) => ({
            ...d,
            soilSamplePointGuid: preserveGuids ? d.soilSamplePointGuid : null,
            soilSamplePointDepthGuid: preserveGuids ? d.soilSamplePointDepthGuid : null,
        })) ?? [],
});

export const applyClassBreaksToUnionZones = (
    rawUnionZones: models.AgEventArea[],
    collapsingEventDetails: models.EventDetails[],
    mergeIntoZonesByGuid: Map<string, models.AgEventArea>
): models.AgEventArea[] => {
    const includedCollapsingZonesWithShape = collapsingEventDetails
        .flatMap((d) => d.eventAreaList)
        .filter((c) => c.applyEventToArea && c.zonePolygons?.length)
        .map((c) => ({
            zone: c,
            shape: getZoneShape(c),
        }))
        .filter((c) => c.shape != null);
    return rawUnionZones.map((z) => {
        const originalZone = mergeIntoZonesByGuid.get(z.eventAreaGuid);
        if (!originalZone || originalZone.applyEventToArea || !z.applyEventToArea) {
            return z;
        }
        const zShape = getZoneShape(z);
        const promotingEntry = includedCollapsingZonesWithShape.find(
            (c) =>
                zShape != null &&
                GeometryMath.almostEqualPolygons(c.shape, zShape, { areaRatioTolerance: 0.01 })
        );
        if (promotingEntry?.zone.eventAreaClassBreak) {
            return { ...z, eventAreaClassBreak: promotingEntry.zone.eventAreaClassBreak };
        }
        return z;
    });
};

const onValidateMergeEvents = function* (userGuid, model) {
    yield call(AgEventAPI.validateMergeAgEvents, userGuid, model);
};

const onMergeEvents = function* (userGuid, model, _action) {
    yield call(AgEventAPI.mergeMultiAgEvents, userGuid, model);
};

const onMergeSoilEvents = function* (userGuid, model, action) {
    const { mergeableEvents } = action.payload;

    const { fieldGuidToEventDetails, soilSampleResults } = yield select(selectors.getModuleState);
    const mergeIntoDetails = fieldGuidToEventDetails.get(mergeableEvents.mergeInto.fieldGuid);

    const defaultDepthConfig = yield retrieveDefaultDepthConfig(
        mergeIntoDetails.agEventTypeList[0].sampleTypeGuid
    );

    const rawDetailsWithZones = yield call(
        EventAPI.fetchEventDetailsWithZones,
        userGuid,
        mergeableEvents.all.map((e) => e.agEventGeneralGuid)
    );
    const allDetailsWithZones: models.EventDetails[] =
        buildEventDetailsWithZones(rawDetailsWithZones);

    const mergeIntoDetailsWithZones = allDetailsWithZones[0];
    const collapsingEventDetails = allDetailsWithZones.slice(1);

    const rawUnionZones = buildUnionZones(
        mergeIntoDetailsWithZones.eventAreaList,
        collapsingEventDetails
    );

    const mergeIntoZonesByGuid = new Map(
        mergeIntoDetailsWithZones.eventAreaList.map((a) => [a.eventAreaGuid, a])
    );
    const unionZones = applyClassBreaksToUnionZones(
        rawUnionZones,
        collapsingEventDetails,
        mergeIntoZonesByGuid
    );

    let mergeIntoEventAreaList = unionZones;
    const mergeIntoWithZonesGuidSet = new Set(
        mergeIntoDetailsWithZones.eventAreaList.map((a) => a.eventAreaGuid)
    );
    const mergeIntoZone1Model = mergeIntoDetails.eventAreaList[0]?.agEventList?.[0]
        ?.agEventModel as models.SampleSetup;

    const baseAgEvent = mergeIntoEventAreaList[0]?.agEventList?.[0];
    const baseAgEventGuid = (baseAgEvent?.agEventModel as models.SampleSetup)
        ?.agEventGuid as string;
    const basePoints = ((baseAgEvent?.agEventModel as models.SampleSetup)?.samplePoints ??
        []) as AgEventAPI.ISoilSamplePoint[];

    let { nextSampleId, nextSequenceId } = findMaxPointIds(basePoints);

    const collapsingPoints: AgEventAPI.ISoilSamplePoint[] = [];
    for (const collapsingDetails of collapsingEventDetails) {
        const collapsingZone0 = collapsingDetails.eventAreaList[0];
        const zonePoints = ((collapsingZone0?.agEventList?.[0]?.agEventModel as models.SampleSetup)
            ?.samplePoints ?? []) as AgEventAPI.ISoilSamplePoint[];

        for (const p of zonePoints) {
            nextSampleId += 1;
            nextSequenceId += 1;
            collapsingPoints.push(
                renumberSoilPoint(p, baseAgEventGuid, nextSampleId, nextSequenceId, true)
            );
        }
    }

    mergeIntoEventAreaList = mergeIntoEventAreaList.map((zone, index) => {
        if (index === 0 && collapsingPoints.length > 0) {
            return {
                ...zone,
                agEventList: [
                    {
                        ...zone.agEventList[0],
                        agEventModel: zone.agEventList[0].agEventModel.updateAgEventModel({
                            samplePoints: [...basePoints, ...collapsingPoints],
                        }),
                    },
                ],
            };
        }
        if (!mergeIntoWithZonesGuidSet.has(zone.eventAreaGuid)) {
            return { ...zone, eventAreaGuid: uuid() };
        }
        return zone;
    });

    const reduxInclusionByAreaId = new Map<number, boolean>(
        mergeIntoDetails.eventAreaList.map((ea) => [ea.eventAreaId, ea.applyEventToArea])
    );
    mergeIntoEventAreaList = mergeIntoEventAreaList.map((zone) => {
        const isIncluded = reduxInclusionByAreaId.get(zone.eventAreaId);
        if (isIncluded != null) {
            return { ...zone, applyEventToArea: isIncluded };
        }
        return zone;
    });

    const mergeIntoGeneralGuid = mergeableEvents.mergeInto.agEventGeneralGuid;
    mergeIntoEventAreaList = mergeIntoEventAreaList.map((zone) => ({
        ...zone,
        eventGeneralGuid: mergeIntoGeneralGuid,
    }));

    const currentAgEventModel = mergeIntoDetails.eventAreaList[0]?.agEventList?.[0]
        ?.agEventModel as models.SampleSetup;
    if (currentAgEventModel != null && mergeIntoEventAreaList[0]?.agEventList?.length > 0) {
        const zone0 = mergeIntoEventAreaList[0];
        const patchedAgEventModel = zone0.agEventList[0].agEventModel.updateAgEventModel({
            interpolationTypeId: currentAgEventModel.interpolationTypeId,
        });
        mergeIntoEventAreaList = [
            {
                ...zone0,
                agEventList: [
                    {
                        ...zone0.agEventList[0],
                        agEventModel: patchedAgEventModel,
                    },
                ],
            },
            ...mergeIntoEventAreaList.slice(1),
        ];
    }

    const mergeIntoWithUnionZones = models.EventDetails.updateEventDetails(mergeIntoDetails, {
        eventAreaList: mergeIntoEventAreaList,
    });

    if (
        mergeIntoZone1Model?.interpolationTypeId === InterpolationType.ZONE_SAMPLING &&
        soilSampleResults == null &&
        !mergeIntoWithUnionZones.hasOneSampleIdPerZone
    ) {
        yield put(actions.saveEventDetailsFailed([2076]));
        yield put(eventInfoActions.setEventDetailsLoading(false));
        yield put(eventListActions.setEventPanelLoading(false));
        return "zone sampling validation failed";
    }

    const blankHeadings = blankClassBreakHeadingsIfNeeded(
        [mergeIntoWithUnionZones, ...collapsingEventDetails].map((d) => d.eventAreaList)
    );

    const mergeIntoWithBlankedHeadings = models.EventDetails.updateEventDetails(
        mergeIntoWithUnionZones,
        { eventAreaList: blankHeadings(mergeIntoWithUnionZones.eventAreaList) }
    );
    const mergeIntoAgEventGeneral = models.EventDetails.groomEventDetailsForAPICall(
        models.EventDetails.updateEventDetails(mergeIntoWithBlankedHeadings, {
            startDate: `${mergeIntoDetails.momentStartDate.format("YYYY-MM-DD")}T20:00:00.000Z`,
            startTime: mergeIntoDetails.momentStartDate.format("h:mma"),
            endDate: `${mergeIntoDetails.momentEndDate.format("YYYY-MM-DD")}T20:00:00.000Z`,
            endTime: mergeIntoDetails.momentEndDate.format("h:mma"),
            notes: [
                `Merged Event from [${mergeIntoDetailsWithZones.eventName}] ${collapsingEventDetails
                    .map((x) => `[${x.eventName}]`)
                    .join(" ")}`,
                ...[mergeIntoDetailsWithZones, ...collapsingEventDetails]
                    .filter((d) => d.notes && String(d.notes).trim().length > 0)
                    .map((d) => `[${d.eventName}]: ${d.notes}`),
            ].join("\n"),
        }),
        defaultDepthConfig
    );

    model = {
        agEventGeneralList: [mergeIntoAgEventGeneral],
        mergeEventGuids: mergeableEvents.collapsing.map((event) => event.agEventGeneralGuid),
    };

    yield call(AgEventAPI.mergeAgEvents, userGuid, model);
    yield put(eventInfoActions.setEventDetailsLoading(false));
    yield put(eventInfoActions.closeEventInfo());
};

const mergeableEventsTypeToApiCall = (type) => {
    switch (type) {
        case MergeableEventType.Application:
        case MergeableEventType.Harvest:
        case MergeableEventType.Planting:
            logFirebaseEvent(`merge_events_${type.toString()}`);
            return onMergeEvents;
        case MergeableEventType.Soil:
            logFirebaseEvent("merge_events_sampling");
            return onMergeSoilEvents;
        default:
            throw new Error("Unknown mergeable event type.");
    }
};

export const onCalibrationRollbackWarning = function* (): Generator<PutEffect<any>, void, any> {
    yield put(
        notificationActions.pushToasterMessage(
            messages.calibrationRollbackWarning,
            MSGTYPE.ERROR,
            undefined,
            false
        )
    );
};

export const onValidateEventsMerge = function* (
    action: ReturnType<typeof actions.validateEventsMerge>
): any {
    const mergeableEvents = action.payload.mergeableEvents as MergeableEvents;
    yield put(eventListActions.setEventPanelLoading(true));

    if (mergeableEvents.type === MergeableEventType.Soil) {
        const userGuid = yield select(getTheUserGuid);
        try {
            const rawEventDetails = yield call(
                EventAPI.fetchEventDetailsWithZones,
                userGuid,
                mergeableEvents.all.map((event) => event.agEventGeneralGuid)
            );

            const allEventDetails = buildEventDetailsWithZones(rawEventDetails);

            const getInterpolationType = (details: models.EventDetails) =>
                details.eventAreaList[0]?.agEventList[0]?.agEventModel?.interpolationTypeId;
            const firstInterpolationType = getInterpolationType(allEventDetails[0]);

            const getMergeErrorCode = (defaultCode: number): number => {
                const hasZoneSampling = allEventDetails.some(
                    (d) => getInterpolationType(d) === InterpolationType.ZONE_SAMPLING
                );
                const hasZoneInterpolation = allEventDetails.some(
                    (d) => getInterpolationType(d) === InterpolationType.ZONE_INTERPOLATION
                );
                if (hasZoneSampling && !hasZoneInterpolation) {
                    return 5002;
                }
                if (hasZoneInterpolation && !hasZoneSampling) {
                    return 5001;
                }
                return defaultCode;
            };
            if (firstInterpolationType == null) {
                yield put(showErrors([getMergeErrorCode(5004)]));
                yield put(actions.validateEventsFailed());
                return;
            }
            const allSameInterpolationType = allEventDetails.every(
                (details) => getInterpolationType(details) === firstInterpolationType
            );
            if (!allSameInterpolationType) {
                yield put(showErrors([getMergeErrorCode(5004)]));
                yield put(actions.validateEventsFailed());
                return;
            }

            const isEventClassified = (details: models.EventDetails) =>
                details.eventAreaList.length > 0 &&
                details.eventAreaList.every((area) => area.eventAreaClassBreak != null);
            const firstEventIsClassified = isEventClassified(allEventDetails[0]);
            const allSameClassifiedStatus = allEventDetails.every(
                (details) => isEventClassified(details) === firstEventIsClassified
            );
            if (!allSameClassifiedStatus) {
                yield put(showErrors([getMergeErrorCode(5005)]));
                yield put(actions.validateEventsFailed());
                return;
            }

            const getIncludedPolygons = (details) =>
                details.eventAreaList
                    .filter((area) => area.applyEventToArea)
                    .map((area) => getZoneShape(area))
                    .filter(Boolean);

            const includedPolygonsPerEvent = allEventDetails.map(getIncludedPolygons);

            const allZoneSampling = firstInterpolationType === InterpolationType.ZONE_SAMPLING;
            if (allZoneSampling) {
                const hasOverlap = includedPolygonsPerEvent.some((polysI, i) =>
                    includedPolygonsPerEvent
                        .slice(i + 1)
                        .some((polysJ) =>
                            polysI.some((polyI) =>
                                polysJ.some((polyJ) => polygonsOverlap(polyI, polyJ))
                            )
                        )
                );
                if (hasOverlap) {
                    yield put(showErrors([5003]));
                    yield put(actions.validateEventsFailed());
                    return;
                }
            }

            const zonesMatchForPair = (areasI, areasJ) =>
                areasI.length === areasJ.length &&
                areasI.every((areaI) => {
                    const polyI = getZoneShape(areaI);
                    if (!polyI) {
                        return false;
                    }
                    return areasJ.some((areaJ) => {
                        const polyJ = getZoneShape(areaJ);
                        if (!polyJ) {
                            return false;
                        }
                        return GeometryMath.almostEqualPolygons(polyI, polyJ, {
                            areaRatioTolerance: 0.01,
                        });
                    });
                });

            const hasZoneMismatch = allEventDetails.some((eventI, i) =>
                allEventDetails.slice(i + 1).some((eventJ, j) => {
                    const polysI = includedPolygonsPerEvent[i];
                    const polysJ = includedPolygonsPerEvent[i + 1 + j];
                    const anyOverlap = polysI.some((polyI) =>
                        polysJ.some((polyJ) => polygonsOverlap(polyI, polyJ))
                    );
                    return (
                        anyOverlap && !zonesMatchForPair(eventI.eventAreaList, eventJ.eventAreaList)
                    );
                })
            );

            if (hasZoneMismatch) {
                yield put(showErrors([5003]));
                yield put(actions.validateEventsFailed());
                return;
            }

            yield put(actions.validateEventsSucceeded());
        } catch (err) {
            console.error(err);
            yield put(notificationActions.apiCallError(err, action));
            yield put(actions.validateEventsFailed());
        }
        return;
    }

    const eventGeneralGuids = mergeableEvents.all.map((event) => event.agEventGeneralGuid);
    const model = {
        eventGeneralGuids,
        eventType: mergeableEvents.type,
    };
    const userGuid = yield select(getTheUserGuid);
    try {
        yield onValidateMergeEvents(userGuid, model);
        yield put(actions.validateEventsSucceeded());
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
        yield put(actions.validateEventsFailed());
    }
};

// return on this function fickle, running as any for now
export const onMergeEventsSave = function* (
    action: ReturnType<typeof actions.mergeEventsSave>
): any {
    const mergeableEvents = action.payload.mergeableEvents as MergeableEvents;
    yield put(eventListActions.setEventPanelLoading(true));

    const eventGeneralGuids = mergeableEvents.all.map((event) => event.agEventGeneralGuid);
    const model = {
        eventGeneralGuids,
        eventType: mergeableEvents.type,
    };

    const userGuid = yield select(getTheUserGuid);
    try {
        const apiCall = mergeableEventsTypeToApiCall(mergeableEvents.type);
        const result = yield apiCall(userGuid, model, action);
        if (result === false) {
            yield put(actions.mergeEventsFailed());
            return;
        } else if (result === "zone sampling validation failed") {
            return;
        }
        yield put(actions.mergeEventsSucceeded(mergeableEvents));
        if (mergeableEvents.type === MergeableEventType.Soil) {
            yield put(actions.fetchSelectedFieldEvents());
            yield take(actions.FETCH_EVENTS_SUCCEEDED);
            yield put(eventInfoActions.removeInFlightCollapsingEvents(eventGeneralGuids));
            yield put(eventListActions.setEventPanelLoading(false));
            if ((mergeableEvents as MergeableSoilEvents).hasSoilSampleResults) {
                const fieldGuid = mergeableEvents.mergeInto.fieldGuid;
                const mergeIntoGuid = mergeableEvents.mergeInto.agEventGeneralGuid;
                const { fieldGuidToEventListMap } = yield select(selectors.getModuleState);
                const mergedEvent = fieldGuidToEventListMap
                    .get(fieldGuid)
                    ?.find((e) => e.agEventGeneralGuid === mergeIntoGuid);
                if (mergedEvent) {
                    yield put(
                        actions.mergeBatchEventSummaries(
                            Immutable.OrderedMap([
                                [fieldGuid, [mergedEvent.updateImportedStatus(9)]],
                            ])
                        )
                    );
                }
            }
        }
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
        yield put(actions.mergeEventsFailed());
    }
};

export const onValidateEventsMergeSucceeded = function* (): Generator<
    CallEffect | SelectEffect | PutEffect<any>,
    void,
    any
> {
    yield put(eventListActions.setEventPanelLoading(false));
    yield put(eventListActions.showMergeModal());
};

export const onMergeEventsFailed = function* (): Generator<
    CallEffect | SelectEffect | PutEffect<any>,
    void,
    any
> {
    yield put(eventListActions.setMergeableEvents(null));
    yield put(eventListActions.setEventPanelLoading(false));
    yield put(actions.clearLastSelectedDisplaySurface());
};

export const onResetCurrentAgEventAreaAgEvent = function* (
    action: ReturnType<typeof actions.resetCurrentAgEventAreaAgEvent>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const { fieldGuid, agEventTransactionTypeGuid } = action.payload;
    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);
    const { fieldGuidToCurrentAreaId } = yield select(getZonesState);
    const eventDetails = fieldGuidToEventDetails.get(fieldGuid);
    const currentAreaId = fieldGuidToCurrentAreaId.get(fieldGuid);
    const agEventArea = eventDetails.eventAreaList.find(
        (agEventArea) => agEventArea.eventAreaId === currentAreaId
    );
    const agEvent = agEventArea.agEventList.find(
        (agEvent) => agEvent.agEventTransactionTypeGuid === agEventTransactionTypeGuid
    );
    const newAgEvent = models.AgEvent.resetEventModel(agEvent);
    yield put(
        actions.updateCurrentAgEventAreaAgEventModel(
            fieldGuid,
            agEventTransactionTypeGuid,
            newAgEvent.agEventModel
        )
    );
};

export const onSetBatchCopySamplePoints = function* (): Generator<
    CallEffect | SelectEffect | PutEffect<any>,
    void,
    any
> {
    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);
    const fieldGuidList = [...fieldGuidToEventDetails.keys()].filter(
        (fieldGuid) => fieldGuid !== commonModels.BATCH_TEMPLATE_FIELD_GUID
    );

    let allSamplePoints = [];
    let isTissue = false;
    for (const fieldGuid of fieldGuidList) {
        const eventDetails = fieldGuidToEventDetails.get(fieldGuid);
        isTissue = eventDetails.agEventTypeList.some(
            (agEventType) =>
                agEventType?.agEventTransactionTypeName === models.EVENT_TYPE_NAME_SAMPLING_TISSUE
        );

        const agEventModel = getSampleAgEventFromEventDetails(eventDetails);
        allSamplePoints = allSamplePoints.concat(
            agEventModel.samplePoints.map((sp) => ({ ...sp, fieldGuid }))
        );
    }

    yield put(
        mapToolsActions.setActiveToolset(Toolset.SAMPLING_AUTO_PLACE, {
            fields: [{ fieldGuid: commonModels.BATCH_TEMPLATE_FIELD_GUID }],
            existingPoints: allSamplePoints,
            isTissue,
        })
    );
    yield put(actions.setSamplingFieldPointsPlaced(true));
};

export const onRestoreBatchSamplePoints = function* (): Generator<
    CallEffect | SelectEffect | PutEffect<any>,
    void,
    any
> {
    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);
    const fieldGuidList = [...fieldGuidToEventDetails.keys()].filter(
        (fieldGuid) => fieldGuid !== commonModels.BATCH_TEMPLATE_FIELD_GUID
    );

    let allSamplePoints = [];
    let isTissue = false;
    for (const fieldGuid of fieldGuidList) {
        const eventDetails = fieldGuidToEventDetails.get(fieldGuid);
        isTissue = eventDetails.agEventTypeList.some(
            (agEventType) =>
                agEventType.agEventTransactionTypeName === models.EVENT_TYPE_NAME_SAMPLING_TISSUE
        );

        const agEventModel = getSampleAgEventFromEventDetails(eventDetails);
        allSamplePoints = allSamplePoints.concat(
            agEventModel.samplePoints.map((sp) => ({ ...sp, fieldGuid }))
        );
    }
    yield put(
        mapToolsActions.setActiveToolset(Toolset.SAMPLING_AUTO_PLACE, {
            fields: [{ fieldGuid: commonModels.BATCH_TEMPLATE_FIELD_GUID }],
            existingPoints: allSamplePoints,
            isTissue,
        })
    );
};

export const onResetEventAreaPolygons = function* (
    action: ReturnType<typeof actions.resetEventAreaPolygons>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const { fieldGuid } = action.payload;
    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);
    const eventDetails = fieldGuidToEventDetails.get(fieldGuid);
    const eventAreaId = Math.min(
        ...eventDetails.eventAreaList.map((agEventArea) => agEventArea.eventAreaId)
    );
    yield put(recsEventsActions.setCurrentAreaId(fieldGuid, eventAreaId));
};

export const onResetEventAreas = function* (
    action: ReturnType<typeof actions.resetEventAreas>
): Generator<CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const { fieldGuid } = action.payload;
    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);
    const eventDetails = fieldGuidToEventDetails.get(fieldGuid);
    const conversionFactors = yield select(blendingSelectors.getConversionFactors);
    const productBlendPicklists = yield select(blendingSelectors.getProductBlendPicklists);

    // We keep the properties from the first area that has `area.agEventList.length > 0`
    const { eventAreaId } = eventDetails.eventAreaList.find((area) => area.agEventList.length > 0);

    // Setting the polygon to `null` causes the zone tools to initialize the zone w/ the field boundary
    const areaIdToPolygonMap = new Map();
    for (const area of eventDetails.eventAreaList) {
        if (area.eventAreaId === eventAreaId) {
            areaIdToPolygonMap.set(eventAreaId, new Map([[eventAreaId, null]]));
        } else {
            areaIdToPolygonMap.set(area.eventAreaId, new Map());
        }
    }

    yield put(
        actions.updateEventAreaPolygons(
            fieldGuid,
            areaIdToPolygonMap,
            conversionFactors,
            productBlendPicklists
        )
    );

    const newEventDetails = (yield select(selectors.getModuleState)).fieldGuidToEventDetails.get(
        fieldGuid
    );
    const field = {
        fieldGuid: newEventDetails.fieldGuid,
        fieldBoundaryGuid: newEventDetails.fieldBoundaryGuid,
        customerGuid: newEventDetails.customerGuid,
    };
    const agEventModel = getSampleAgEventFromEventDetails(newEventDetails);
    yield put(
        mapToolsActions.setActiveToolsetPayloadOnly({
            recEventDetails: newEventDetails,
            field,
            samplePoints: agEventModel?.samplePoints ?? [],
        })
    );
};

export const onS3PhotoCopyFailed = function* (): Generator<PutEffect<any>, void, any> {
    yield put(notificationActions.pushToasterMessage(messages.s3PhotoCopyFailed, MSGTYPE.WARNING));
};

export const onS3PhotoUploadFailed = function* (): Generator<PutEffect<any>, void, any> {
    yield put(
        notificationActions.pushToasterMessage(messages.s3PhotoUploadFailed, MSGTYPE.WARNING)
    );
};

export const onSaveEventDetails = function* (
    action: ReturnType<typeof actions.saveEventDetails>
): Generator<any[] | boolean | TakeEffect | CallEffect | SelectEffect | PutEffect<any>, void, any> {
    const userGuid = yield select(getTheUserGuid);
    const fieldGuidToEventDetails: Immutable.OrderedMap<string, models.EventDetails> = yield select(
        selectors.getFieldGuidToEventDetailsMap
    );
    const picklistOptionsCroppingSeason = yield select(
        picklistSelectors.getPicklistOptionsFromCode,
        getPickListCode(PICKLIST_CROPPING_SEASON)
    );
    const cropData = yield select(getCropDropdownState);
    const picklistOptionsCropPurpose = yield select(
        picklistSelectors.getPicklistOptionsFromCode,
        getPickListCode(PICKLIST_CROP_PURPOSE)
    );

    const batchFieldGuid = yield select(selectors.getLastEventBatchFieldGuid);
    const firstFieldEvents = fieldGuidToEventDetails.first();
    const isUpdate =
        firstFieldEvents && firstFieldEvents.agEventGeneralGuid !== "" && !batchFieldGuid;

    const fieldGuidToEventDetailsAdjusted = !isUpdate
        ? new Map<string, models.EventDetails>()
        : fieldGuidToEventDetails;
    if (!isUpdate) {
        for (const [fieldGuid, eventDetails] of fieldGuidToEventDetails) {
            const revisedEventDetails = models.EventDetails.updateEventDetails(eventDetails, {
                agEventGeneralGuid: uuid(),
            });
            fieldGuidToEventDetailsAdjusted.set(fieldGuid, revisedEventDetails);
        }
    }
    let eventDetailsList: models.EventDetails[] = [
        ...fieldGuidToEventDetailsAdjusted.values(),
    ].filter((eventDetails) => eventDetails.fieldGuid !== commonModels.BATCH_TEMPLATE_FIELD_GUID);
    const sampleTypeGuid = eventDetailsList.find((e) => e.isSamplingEvent)?.agEventTypeList[0]
        .sampleTypeGuid;

    if (sampleTypeGuid && hasInvalidZoneSamplingEvent(eventDetailsList)) {
        // Error code 2076: Zone sampling requires one sample id per zone
        yield put(actions.saveEventDetailsFailed([2076]));
        yield put(eventInfoActions.setEventDetailsLoading(false));
        return;
    }

    const fieldMap: Immutable.Map<string, FieldInfo> = yield select(cdSelectors.getFieldMap);

    const fieldGuidToNewEventSummariesMap = Immutable.OrderedMap<
        string,
        models.AgEventSummary[]
    >().withMutations((map) => {
        for (const [fieldGuid, eventDetails] of fieldGuidToEventDetailsAdjusted) {
            if (
                fieldGuid !== commonModels.BATCH_TEMPLATE_FIELD_GUID &&
                fieldGuid !== batchFieldGuid
            ) {
                const newEventSummaries = getAgEventSummaryListFromEventDetails(
                    [eventDetails],
                    picklistOptionsCroppingSeason,
                    cropData,
                    picklistOptionsCropPurpose,
                    fieldMap
                );
                map.set(fieldGuid, newEventSummaries);
            }
        }
    });

    yield put(actions.mergeBatchEventSummaries(fieldGuidToNewEventSummariesMap));

    let defaultDepthConfig = yield select(getUserDepthConfig);

    if (defaultDepthConfig == null && sampleTypeGuid) {
        yield put(sampleActions.fetchSampleSoilEventDefaults(sampleTypeGuid));
        yield take(sampleActions.SET_USER_DEPTH_CONFIG);
        defaultDepthConfig = yield select(getUserDepthConfig);
    }
    if (yield hasScoutingEvent(eventDetailsList)) {
        eventDetailsList = yield filterScoutingEvent(eventDetailsList);
    }
    // Update the times strings to match the format expected for upload
    eventDetailsList = eventDetailsList.map((eventDetails) =>
        models.EventDetails.groomEventDetailsForAPICall(
            models.EventDetails.updateEventDetails(eventDetails, {
                startDate: `${eventDetails.momentStartDate.format("YYYY-MM-DD")}T20:00:00.000Z`,
                startTime: eventDetails.momentStartDate.format("h:mma"),
                endDate: `${eventDetails.momentEndDate.format("YYYY-MM-DD")}T20:00:00.000Z`,
                endTime: eventDetails.momentEndDate.format("h:mma"),
            }),
            defaultDepthConfig
        )
    );

    if (!isUpdate) {
        const fieldInfoList = eventDetailsList
            .map((eventDetails) => fieldMap.get(eventDetails.fieldGuid))
            .filter((field) => field != null);
        const fieldGuidToCountMap = new Map();
        for (const { fieldGuid, eventCount } of fieldInfoList) {
            fieldGuidToCountMap.set(fieldGuid, eventCount + 1);
        }
        const countUpdatePayload = [...fieldGuidToCountMap.entries()].map(
            ([fieldGuid, eventCount]) => ({
                fieldGuid,
                eventCount,
            })
        );
        yield put(cdActions.batchUpdateFieldEventCount(countUpdatePayload));
    }

    try {
        yield call(
            isUpdate ? AgEventAPI.updateAgEvents : AgEventAPI.addAgEvents,
            userGuid,
            eventDetailsList
        );
    } catch (err) {
        if (!(err instanceof APIErrorWithCode)) {
            yield put(notificationActions.apiCallError(err, action));
        } else if (eventDetailsList.length === 1) {
            yield put(actions.saveEventDetailsFailed(err.errorCodeList, err.model));
        } else {
            // batch session may have partially succeeded, so show error message for failed Events
            yield put(
                notificationActions.pushToasterMessage(
                    messages.batchEventsFailed,
                    MSGTYPE.ERROR,
                    { Error: err.errorCodeList },
                    10000
                )
            );
        }
        yield put(eventInfoActions.setEventDetailsLoading(false));
        return;
    }
    yield put(eventInfoActions.closeEventInfo());
    yield put(actions.saveEventDetailsSucceeded());
    yield put(eventInfoActions.setEventDetailsLoading(false));
};

const onSetEventSamplePoints = function* (action) {
    const { samplePoints }: { samplePoints: AgEventAPI.ISoilSamplePoint[] } = action.payload;
    const { fieldGuidToEventDetails, soilSampleResults, tissueSampleResults } = yield select(
        selectors.getModuleState
    );
    if (fieldGuidToEventDetails.size > 1) {
        //handle batch case
        const fieldGuidList = [...fieldGuidToEventDetails.keys()].filter(
            (fieldGuid) => fieldGuid !== commonModels.BATCH_TEMPLATE_FIELD_GUID
        );

        for (const fieldGuid of fieldGuidList) {
            const eventDetails = fieldGuidToEventDetails.get(fieldGuid);
            const agEventModel = getSampleAgEventFromEventDetails(eventDetails);

            const newPointDefaultProps = {
                productivityRatingGuid: "",
                samplePointDepths:
                    agEventModel.samplePoints.length > 0 &&
                    agEventModel.samplePoints[0].samplePointDepths
                        ? agEventModel.samplePoints[0].samplePointDepths
                        : null,
                cropGrowthStageGuid: null,
                plantPartGuid: null,
            };

            const isTissue =
                agEventModel.samplePoints.some(
                    (sampleItem) => sampleItem.eventSampleTissuePointGuid !== undefined
                ) ||
                eventDetails.agEventTypeList.some(
                    (agEventType) =>
                        agEventType?.agEventTransactionTypeName ===
                        models.EVENT_TYPE_NAME_SAMPLING_TISSUE
                );

            if (isTissue) {
                newPointDefaultProps.cropGrowthStageGuid = yield select(
                    sampleTissueSelectors.getCropGrowthStageGuid
                );
                newPointDefaultProps.plantPartGuid = yield select(
                    sampleTissueSelectors.getPlantPartGuid
                );
            }
            const guidProp = isTissue
                ? models.samplePointGuidProperty.TISSUE
                : models.samplePointGuidProperty.SOIL;
            const applicableSamplePoints = samplePoints.filter((pt) => pt.fieldGuid === fieldGuid);
            if (applicableSamplePoints.length < 1) {
                continue;
            }
            const pointGuidToPointMap = new Map(
                agEventModel.samplePoints.map((samplePoint) => [samplePoint[guidProp], samplePoint])
            );
            const newSamplePoints = applicableSamplePoints.map((samplePoint) => {
                const existingPoint: any = pointGuidToPointMap.has(samplePoint[guidProp])
                    ? pointGuidToPointMap.get(samplePoint[guidProp])
                    : newPointDefaultProps;
                return {
                    ...(existingPoint as any),
                    ...samplePoint,
                    cropGrowthStageGuid: existingPoint.cropGrowthStageGuid,
                    plantPartGuid: existingPoint.plantPartGuid,
                };
            });
            yield put(
                actions.updateSamplingAgEventModel(fieldGuid, {
                    samplePoints: newSamplePoints,
                })
            );
        }
    } else {
        const fieldGuidToEventDetailsValue = fieldGuidToEventDetails.entries().next().value;
        if (fieldGuidToEventDetailsValue === undefined) {
            return;
        }
        const [fieldGuid, eventDetails] = fieldGuidToEventDetailsValue;

        const agEventModel = getSampleAgEventFromEventDetails(eventDetails);

        const newPointDefaultProps = {
            productivityRatingGuid: "",
            samplePointDepths:
                agEventModel.samplePoints.length > 0 &&
                agEventModel.samplePoints[0].samplePointDepths
                    ? agEventModel.samplePoints[0].samplePointDepths
                    : null,
        };

        const isTissue = agEventModel.samplePoints.some(
            (sampleItem) => sampleItem.eventSampleTissuePointGuid !== undefined
        );
        const guidProp = isTissue
            ? models.samplePointGuidProperty.TISSUE
            : models.samplePointGuidProperty.SOIL;

        const pointGuidToPointMap = new Map<
            string,
            AgEventAPI.ISoilSamplePoint | AgEventAPI.ITissueSamplePoint
        >(agEventModel.samplePoints.map((samplePoint) => [samplePoint[guidProp], samplePoint]));
        const newSamplePoints = samplePoints.map((samplePoint) => {
            const isExistingPoint = pointGuidToPointMap.has(samplePoint[guidProp]);
            const existingPoint = pointGuidToPointMap.get(samplePoint[guidProp]);
            return {
                ...(isExistingPoint ? existingPoint : newPointDefaultProps),
                ...samplePoint,
                isNew: !isExistingPoint || existingPoint?.isNew === true,
                ...(isTissue && {
                    cropGrowthStageGuid: (existingPoint as AgEventAPI.ITissueSamplePoint)
                        ?.cropGrowthStageGuid,
                    plantPartGuid: (existingPoint as AgEventAPI.ITissueSamplePoint)?.plantPartGuid,
                }),
            };
        });
        if (soilSampleResults) {
            yield* updateSoilResults(newSamplePoints);
        } else if (tissueSampleResults) {
            yield* updateTissueResults(newSamplePoints);
        }
        yield put(
            actions.updateSamplingAgEventModel(fieldGuid, {
                samplePoints: newSamplePoints,
            })
        );
    }
};

function* updateSoilResults(samplePoints) {
    //Determine addition or subtraction of points
    const { soilSampleResults, soilSampleResultsOriginal } = yield select(selectors.getModuleState);
    let revisedSampleResults = {
        gridHeader: [...soilSampleResults.gridHeader],
        gridData: [...soilSampleResults.gridData],
    };
    let totalDepths = 0;
    samplePoints.forEach((p) => {
        const depthAdd = p.samplePointDepths ? p.samplePointDepths?.length : 0;
        totalDepths += depthAdd;
    });
    if (soilSampleResults?.gridHeader.length > totalDepths + 1) {
        //sample point was removed
        revisedSampleResults = yield updateSampleResult(soilSampleResults, samplePoints, false);
    } else if (
        soilSampleResults?.gridHeader.length < totalDepths + 1 &&
        soilSampleResultsOriginal
    ) {
        //sample point was added
        revisedSampleResults = yield addToSampleResults(
            soilSampleResults,
            soilSampleResultsOriginal,
            samplePoints,
            revisedSampleResults
        );
    }
    yield put(actions.updateSoilSampleResults(revisedSampleResults));
}

function* updateTissueResults(samplePoints) {
    //Determine addition or subtraction of points
    const { tissueSampleResults, tissueSampleResultsOriginal } = yield select(
        selectors.getModuleState
    );
    let revisedSampleResults = {
        gridHeader: [...tissueSampleResults.gridHeader],
        gridData: [...tissueSampleResults.gridData],
    };
    if (tissueSampleResults?.gridHeader.length > samplePoints.length + 1) {
        //sample point with results was removed
        revisedSampleResults = yield updateSampleResult(tissueSampleResults, samplePoints, true);
    } else if (
        tissueSampleResults?.gridHeader.length < samplePoints.length + 1 &&
        tissueSampleResultsOriginal
    ) {
        //sample point was added
        revisedSampleResults = yield addToSampleResults(
            tissueSampleResults,
            tissueSampleResultsOriginal,
            samplePoints,
            revisedSampleResults
        );
    }
    yield put(actions.updateTissueSampleResults(revisedSampleResults));
}

const addToSampleResults = (
    sampleResults,
    sampleResultsOriginal,
    samplePoints,
    revisedSampleResults
) => {
    //Determine type of add, then point to appropriate add function
    const columnNames = sampleResults.gridHeader.map((p) => p.columnName.replace(/[^\d-]/g, ""));
    const isComposite = columnNames[1]?.includes("-");
    const totalDepths = samplePoints[0]?.samplePointDepths?.length;
    const isMultiDepth = totalDepths > 1;
    const newPoint = samplePoints.find((p) => {
        const pointAttribute = isComposite
            ? p.sampleId.toString() + "-" + p.sequenceId.toString()
            : p.sampleId.toString();
        return !columnNames.includes(pointAttribute);
    });
    const applicableId = isComposite
        ? newPoint.sampleId.toString() + "-" + newPoint.sequenceId.toString()
        : newPoint.sampleId.toString();
    if (newPoint.isNew) {
        //new point was added
        revisedSampleResults = isMultiDepth
            ? addMultiDepthSampleResult(sampleResults, applicableId, totalDepths)
            : addSampleResult(sampleResults, applicableId);
    } else {
        //point with results was re-added through Undo
        revisedSampleResults = isMultiDepth
            ? reAddMultiDepthSampleResult(
                  sampleResults,
                  applicableId,
                  totalDepths,
                  sampleResultsOriginal
              )
            : reAddSampleResult(sampleResults, applicableId, sampleResultsOriginal);
    }
    return revisedSampleResults;
};

const addSampleResult = (sampleResults, applicableId) => {
    //Sort through and add appropriate results to each grid data item
    const revisedGridHeader = [
        ...sampleResults.gridHeader,
        {
            columnName: applicableId,
            hasResults: false,
            hasMatch: false,
        },
    ];
    const revisedGridData = [];
    for (const attribute of sampleResults.gridData) {
        const revisedValues = [];
        for (const value of attribute.sampleResultValues) {
            revisedValues.push(value);
        }
        revisedValues.push({
            samplePointSequenceDepthId: applicableId,
            sampleResultValue: null,
        });
        const revisedAtt = {
            actualAttributeName: attribute.actualAttributeName,
            sampleAttributeName: attribute.sampleAttributeName,
            sampleResultValues: revisedValues,
        };
        revisedGridData.push(revisedAtt);
    }
    return sortGridHeader(revisedGridHeader, revisedGridData);
};

const reAddSampleResult = (sampleResults, applicableId, sampleResultsOriginal) => {
    //Add back a point that previously existed, along with the results data
    const matchingHeader = sampleResultsOriginal.gridHeader.find(
        (gh) => gh.columnName === applicableId
    );
    const revisedGridHeader = [...sampleResults.gridHeader, matchingHeader];
    const revisedGridData = [];
    for (const attribute of sampleResults.gridData) {
        const revisedValues = [];
        const originalAttribute = sampleResultsOriginal.gridData.find(
            (gd) => gd.sampleAttributeName === attribute.sampleAttributeName
        );
        const originalResult =
            originalAttribute.sampleResultValues.find(
                (a) => a.samplePointSequenceDepthId === applicableId
            ) || {};
        for (const value of attribute.sampleResultValues) {
            revisedValues.push(value);
        }
        revisedValues.push({
            samplePointSequenceDepthId: originalResult.samplePointSequenceDepthId,
            sampleResultValue: originalResult.sampleResultValue,
        });
        revisedValues.sort((a, b) =>
            new Intl.Collator("en", { numeric: true }).compare(
                a.samplePointSequenceDepthId,
                b.samplePointSequenceDepthId
            )
        );
        const revisedAtt = {
            actualAttributeName: attribute.actualAttributeName,
            sampleAttributeName: attribute.sampleAttributeName,
            sampleResultValues: revisedValues,
        };
        revisedGridData.push(revisedAtt);
    }
    return sortGridHeader(revisedGridHeader, revisedGridData);
};

const addMultiDepthSampleResult = (sampleResults, applicableId, totalDepths) => {
    //Add a multi-depth soil sample point, with appropriate number of results
    let idx = 0;
    const newGridHeaders = [];
    const isMultiDepth = totalDepths > 1;
    do {
        const columnName = isMultiDepth
            ? applicableId.toString() + multiDepthIds[idx + 1]
            : applicableId;
        newGridHeaders.push({
            columnName,
            hasResults: false,
            hasMatch: false,
        });
        idx++;
    } while (idx < totalDepths);

    const revisedGridHeader = [...sampleResults.gridHeader, ...newGridHeaders];
    const revisedGridData = [];
    for (const attribute of sampleResults.gridData) {
        const revisedValues = [];
        for (const value of attribute.sampleResultValues) {
            revisedValues.push(value);
        }
        idx = 0;
        do {
            const applicableDepthId = isMultiDepth
                ? applicableId.toString() + multiDepthIds[idx + 1]
                : applicableId;

            revisedValues.push({
                samplePointSequenceDepthId: applicableDepthId,
                sampleResultValue: null,
            });
            idx++;
        } while (idx < totalDepths);

        const revisedAtt = {
            actualAttributeName: attribute.actualAttributeName,
            sampleAttributeName: attribute.sampleAttributeName,
            sampleResultValues: revisedValues,
        };
        revisedGridData.push(revisedAtt);
    }
    return sortGridHeader(revisedGridHeader, revisedGridData);
};

const reAddMultiDepthSampleResult = (
    sampleResults,
    applicableId,
    totalDepths,
    sampleResultsOriginal
) => {
    //Add back a point that previously existed, with appropriate number of results
    let revisedSampleResults = sampleResults;
    let idx = 0;
    do {
        const multiDepthId = applicableId.toString() + multiDepthIds[idx + 1];
        revisedSampleResults = reAddSampleResult(
            revisedSampleResults,
            multiDepthId,
            sampleResultsOriginal
        );
        idx++;
    } while (idx < totalDepths);
    return revisedSampleResults;
};

const sortGridHeader = (revisedGridHeader, revisedGridData) => {
    //Sort grid header based on depth ids or applicable sequence ids
    const firstGridHeader = revisedGridHeader.find((h) => h.columnName === "Sample Attribute");
    const remainingGridHeaders = revisedGridHeader
        .filter((h) => h.columnName !== "Sample Attribute")
        .sort((a, b) =>
            new Intl.Collator("en", { numeric: true }).compare(a.columnName, b.columnName)
        );
    const sortedGridHeader = [firstGridHeader, ...remainingGridHeaders];
    return {
        gridData: revisedGridData,
        gridHeader: sortedGridHeader,
    };
};

const updateSampleResult = (sampleResults, samplePoints, isTissue) => {
    //Filter out any points that were removed, along with corresponding results
    const revisedGridHeader = sampleResults.gridHeader.filter((gh) => {
        const sp = samplePoints.some((p) => {
            const pointAttribute = gh.columnName.includes("-")
                ? p.sampleId.toString() + "-" + p.sequenceId.toString()
                : p.sampleId.toString();
            const columnPrefix = gh.columnName.replace(/[^\d-]/g, "");
            return pointAttribute === columnPrefix;
        });
        return sp || gh.columnName === "Sample Attribute";
    });
    const revisedGridData = [];
    for (const attribute of sampleResults.gridData) {
        const revisedValues = attribute.sampleResultValues.filter((v) => {
            return samplePoints.some((p) => {
                const pointAttribute = v.samplePointSequenceDepthId.includes("-")
                    ? p.sampleId.toString() + "-" + p.sequenceId.toString()
                    : p.sampleId.toString();
                const ids = isTissue
                    ? [pointAttribute]
                    : p.samplePointDepths.map((d) => pointAttribute + d.depthId);
                return ids.includes(v.samplePointSequenceDepthId);
            });
        });
        const revisedAtt = {
            actualAttributeName: attribute.actualAttributeName,
            sampleAttributeName: attribute.sampleAttributeName,
            sampleResultValues: revisedValues,
        };
        revisedGridData.push(revisedAtt);
    }
    return {
        gridData: revisedGridData,
        gridHeader: revisedGridHeader,
    };
};

const onSetManualZones = function* (action) {
    const { fieldGuid } = action.payload;
    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);

    const eventDetails = fieldGuidToEventDetails.get(fieldGuid);
    const agEventList = eventDetails.eventAreaList[0].agEventList;

    const applyEventToArea = true; // default for sampling
    let eventAreaList = eventDetails.eventAreaList;
    if (eventAreaList != null) {
        const colorRamps = yield select(getColorRamps);
        const colorRampGuid = yield select(selectors.getManualZoneColorRampGuid);
        const soilTypeColorRamp = colorRamps.find((cr) => cr.colorRampGuid === colorRampGuid);
        const zoneCount = eventDetails?.eventAreaList?.length || 1;
        const layerFileToUse = soilTypeColorRamp?.layerFiles.find(
            (lf) => lf.numberOfClasses === zoneCount
        );
        eventAreaList = eventAreaList.map((area, idx) => {
            const eventList = idx === 0 ? agEventList : [];

            const cb = new ClassBreak();
            cb.classId = area.eventAreaId.toString();
            cb.acreage = area.calculatedArea.toString();
            cb.heading = area.eventAreaId.toString();
            cb.label = area.eventAreaId.toString();
            cb.red = layerFileToUse?.classBreaks[idx].color.r.toString();
            cb.green = layerFileToUse?.classBreaks[idx].color.g.toString();
            cb.blue = layerFileToUse?.classBreaks[idx].color.b.toString();
            cb.isManualClassifiedYn = true;

            return models.AgEventArea.updateAgEventArea(area, {
                agEventList: eventList,
                applyEventToArea,
                eventAreaClassBreak: cb,
            });
        });
        yield put(
            actions.updateEventDetails(fieldGuid, {
                eventAreaList,
            })
        );
    }
    const newEventDetails = (yield select(selectors.getModuleState)).fieldGuidToEventDetails.get(
        fieldGuid
    );
    const field = {
        fieldGuid: newEventDetails.fieldGuid,
        fieldBoundaryGuid: newEventDetails.fieldBoundaryGuid,
        customerGuid: newEventDetails.customerGuid,
    };
    const agEventModel = getSampleAgEventFromEventDetails(newEventDetails);
    yield put(
        mapToolsActions.setActiveToolsetPayloadOnly({
            recEventDetails: newEventDetails,
            field,
            samplePoints: agEventModel?.samplePoints ?? [],
            skipZoneDataUpdate: false,
        })
    );
};

const onSetEventZonesFromLayer = function* (action) {
    const { fieldGuid } = action.payload;
    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);

    const eventDetails = fieldGuidToEventDetails.get(fieldGuid);

    // The way this is coded, this is currently only a supported operation for
    //  sample events, where `agEventList` is ignored except on the first zone.
    console.assert(eventDetails.eventAreaList.length > 0);
    console.assert(
        eventDetails.eventAreaList.every((agEventArea) => agEventArea.agEventList.length === 1)
    );
    console.assert(eventDetails.isSamplingEvent);

    const agEventList = eventDetails.eventAreaList[0].agEventList;

    const applyEventToArea = true; // default for sampling
    let eventAreaList = yield* getEventAreaListFromSurface({
        ...action,
        payload: {
            ...action.payload,
            field: {
                fieldGuid,
            },
        },
    });
    if (eventAreaList != null) {
        eventAreaList = eventAreaList.map((area, idx) => {
            const eventList = idx === 0 ? agEventList : [];
            return models.AgEventArea.updateAgEventArea(area, {
                agEventList: eventList,
                applyEventToArea,
            });
        });
        yield put(
            actions.updateEventDetails(fieldGuid, {
                eventAreaList,
            })
        );
    }

    const newEventDetails = (yield select(selectors.getModuleState)).fieldGuidToEventDetails.get(
        fieldGuid
    );
    const field = {
        fieldGuid: newEventDetails.fieldGuid,
        fieldBoundaryGuid: newEventDetails.fieldBoundaryGuid,
        customerGuid: newEventDetails.customerGuid,
    };
    const agEventModel = getSampleAgEventFromEventDetails(newEventDetails);
    yield put(
        mapToolsActions.setActiveToolsetPayloadOnly({
            recEventDetails: newEventDetails,
            field,
            samplePoints: agEventModel?.samplePoints ?? [],
        })
    );
};

const onSetSamplingEventId = function* (action) {
    const { fieldGuid } = action.payload;
    try {
        const samplingEventIdRequest: AgEventAPI.ISamplingEventIdRequest = {
            blockSize: 1,
            deviceVendorId: null,
            isThirdParty: false,
        };
        const eventId = yield call(AgEventAPI.getNextEventId, samplingEventIdRequest);
        yield put(actions.updateSamplingAgEventModel(fieldGuid, { eventId }));
    } catch (err) {
        yield put(notificationActions.apiCallError(err, action));
    }
};

export const onUpdateCurrentAgEventAreaAgEventModel = function* (
    action: ReturnType<typeof actions.updateCurrentAgEventAreaAgEventModel>
): Generator<SelectEffect | PutEffect<any>, void, any> {
    const { fieldGuid, agEventTransactionTypeGuid, newProps } = action.payload;
    const { fieldGuidToCurrentAreaId } = yield select(getZonesState);
    const agEventAreaId = fieldGuidToCurrentAreaId.get(fieldGuid);
    yield put(
        actions.updateAgEventModel(fieldGuid, agEventAreaId, agEventTransactionTypeGuid, newProps)
    );
};

export const onUpdateEventModel = function* (
    action: ReturnType<typeof actions.updateAgEventModel>
): Generator<SelectEffect | PutEffect<any>, void, any> {
    const { fieldGuid, agEventAreaId, agEventTransactionTypeGuid, newProps } = action.payload;
    yield put(
        actions.setEventModel(fieldGuid, agEventAreaId, agEventTransactionTypeGuid, newProps)
    );
    const enableSave = yield select(selectors.getEnableSave, fieldGuid);
    if (enableSave) {
        yield put(setFieldsBackgroundOnlyBatch(false));
    }
};

const onBatchEditEventsSave = function* (
    action: ReturnType<typeof actions.batchEditEventsSave>
): any {
    const { events, attributeToUpdate } = action.payload;
    yield put(actions.fetchSelectedFieldEvents());
    yield take(actions.FETCH_EVENTS_SUCCEEDED);
    // Season edits complete instantly on the server with no re-surfacing, so no
    // IOT status progression will follow. Leave status at Complete (6).
    if (attributeToUpdate === EventAPI.BatchEditAttributeToUpdate.Season) {
        return;
    }
    const { fieldGuidToEventListMap } = yield select(selectors.getModuleState);
    const fieldGuidMap = new Map<string, models.AgEventSummary[]>();
    for (const { fieldGuid, agEventGeneralGuid } of events) {
        const eventList = fieldGuidToEventListMap.get(fieldGuid);
        const event = eventList?.find((e) => e.agEventGeneralGuid === agEventGeneralGuid);
        if (event && Number(event.importedStatus) === 6) {
            if (!fieldGuidMap.has(fieldGuid)) {
                fieldGuidMap.set(fieldGuid, []);
            }
            fieldGuidMap.get(fieldGuid).push(event.updateImportedStatus(42));
        }
    }
    if (fieldGuidMap.size > 0) {
        yield put(actions.mergeBatchEventSummaries(Immutable.OrderedMap(fieldGuidMap)));
    }
};

export const onUpdateEventSummaryImportedStatus = function* (action) {
    const { fieldGuid, agEventGeneralGuid, importedStatus } = action.payload;
    //TODO: clean up type
    let newProps: any = { importedStatus };
    if (importedStatus === StatusCodes.ProcessingSurfaces) {
        const fieldEventSummaries: AgEventAPI.IAgEventSummary[] = yield call(
            AgEventAPI.fetchSummaries,
            [fieldGuid]
        );
        const newEventSummaryProps = fieldEventSummaries.find(
            (s) => s.agEventGeneralGuid === agEventGeneralGuid
        );
        newProps = {
            importedStatus,
            ...newEventSummaryProps,
        };
    }
    yield put(actions.updateEventSummaryItem(fieldGuid, agEventGeneralGuid, newProps));
};

export const onUpdateBatchDepthConfig = function* (
    action: ReturnType<typeof actions.updateBatchDepthConfig>
): Generator<SelectEffect | PutEffect<any>, void, any> {
    const { depthIAGuid, samplePointDepths } = action.payload;
    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);
    const fieldGuidList = [...fieldGuidToEventDetails.keys()].filter(
        (fieldGuid) => fieldGuid !== commonModels.BATCH_TEMPLATE_FIELD_GUID
    );

    for (const fieldGuid of fieldGuidList) {
        const eventDetails = fieldGuidToEventDetails.get(fieldGuid);
        const agEventModel = getSampleAgEventFromEventDetails(eventDetails);

        const newSamplePoints = agEventModel.samplePoints.map((samplePoint) => {
            return {
                ...samplePoint,
                samplePointDepths,
            };
        });
        yield put(
            actions.updateSamplingAgEventModel(fieldGuid, {
                depthIAGuid,
                samplePoints: newSamplePoints,
            })
        );
    }
};

export const onUpdateBatchSamplingAgEvents = function* (
    action: ReturnType<typeof actions.updateBatchSamplingEvents>
): Generator<SelectEffect | PutEffect<any>, void, any> {
    const { isPointProps, newProps } = action.payload;
    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);
    const fieldGuidList = [...fieldGuidToEventDetails.keys()].filter(
        (fieldGuid) => fieldGuid !== commonModels.BATCH_TEMPLATE_FIELD_GUID
    );

    for (const fieldGuid of fieldGuidList) {
        if (isPointProps) {
            const eventDetails = fieldGuidToEventDetails.get(fieldGuid);
            const agEventModel = getSampleAgEventFromEventDetails(eventDetails);
            const newSamplePoints = agEventModel.samplePoints.map((samplePoint) => {
                return {
                    ...samplePoint,
                    ...newProps,
                };
            });
            yield put(
                actions.updateSamplingAgEventModel(fieldGuid, {
                    samplePoints: newSamplePoints,
                })
            );
        } else {
            yield put(actions.updateSamplingAgEventModel(fieldGuid, newProps));
        }
    }
};

export const onUpdateSamplingAgEventModel = function* (
    action: ReturnType<typeof actions.updateSamplingAgEventModel>
): Generator<SelectEffect | PutEffect<any>, void, any> {
    const { fieldGuid, newProps } = action.payload;
    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);
    const eventDetails = fieldGuidToEventDetails.get(fieldGuid);

    if (!eventDetails) {
        return;
    }
    // sampling events cannot be aggregate events
    console.assert(eventDetails.agEventTypeList.length === 1);
    const agEventTypeInfo = eventDetails.agEventTypeList[0];

    console.assert(eventDetails.eventAreaList.length > 0);
    const area = eventDetails.eventAreaList[0];
    console.assert(area.agEventList.length > 0);
    const { agEventModel } = area.agEventList[0];
    console.assert(agEventModel.sampleTypeGuid === agEventTypeInfo.sampleTypeGuid);

    yield put(
        actions.updateAgEventModel(
            fieldGuid,
            area.eventAreaId,
            agEventTypeInfo.agEventTransactionTypeGuid,
            newProps
        )
    );
};

export const onUpdatePlantingEventModel = function* (
    action: ReturnType<typeof actions.updatePlantingEventModel>
): Generator<SelectEffect | PutEffect<any>, void, any> {
    const { fieldGuid, transactionTypeGuid, newProps } = action.payload;
    const { fieldGuidToEventDetails } = yield select(selectors.getModuleState);
    const eventDetails = fieldGuidToEventDetails.get(fieldGuid);
    eventDetails.eventAreaList.forEach((agEventArea) => {
        agEventArea.agEventList.forEach((agEvent) => {
            const { agEventTransactionTypeGuid, agEventModel } = agEvent;
            if (
                agEventTransactionTypeGuid === transactionTypeGuid &&
                agEventModel.eventPlantingVarietyHybridList.length
            ) {
                agEventModel.eventPlantingVarietyHybridList =
                    agEventModel.eventPlantingVarietyHybridList.map((vh) => {
                        return { ...vh, ...newProps };
                    });
            }
        });
    });

    yield put(
        actions.updateEventDetails(fieldGuid, {
            eventAreaList: eventDetails.eventAreaList,
        })
    );
};

export const eventsSaga = function* (): Generator<AllEffect, void, unknown> {
    yield all([
        takeEvery(actions.ACTIVATE_EVENT, onActivateEvent),
        takeEvery(actions.ADD_AGGREGATE_EVENT, onAddAggregateEvent),
        takeLatest(actions.APPLY_YIELD_CALIBRATION, onApplyYieldCalibration),
        takeLatest(actions.BATCH_COPY_EVENTS, onBatchCopyEvents),
        takeLatest(actions.BATCH_COPY_EVENT_DETAILS, onBatchCopyEventDetails),
        takeLatest(actions.BATCH_SAMPLING_RESET, onBatchSamplingReset),
        takeEvery(actions.CALIBRATION_ROLLBACK_WARNING, onCalibrationRollbackWarning),
        takeEvery(actions.CREATE_NEW_BATCH_EVENT, onCreateNewBatchEvent),
        takeLatest(actions.CREATE_NEW_COPY_BATCH_EVENT, onCreateNewCopyBatchEvent),
        takeLatest(actions.COPY_BATCH_TEMPLATE_TO_EVENTS, onCopyBatchTemplateToEvents),
        takeLatest(actions.COPY_EVENT_DETAILS, onCopyEventDetails),
        takeEvery(actions.CREATE_NEW_CLASSIFIED_EVENT_DETAILS, onCreateNewClassifiedEvent),
        takeEvery(actions.CREATE_NEW_EVENT_DETAILS, onCreateNewEventDetails),
        takeEvery(actions.DELETE_TEMPORARY_SCOUTING_PHOTOS, onDeleteTemporaryScoutingPhotos),
        takeEvery(actions.DELETE_EVENTS, onDeleteEvents),
        takeLatest(actions.FETCH_EVENT_DETAILS, onFetchEventDetails),
        takeEvery(actions.FETCH_EVENT_DETAILS_SUCCEEDED, onFetchEventDetailsSucceeded),
        takeLatest(actions.FETCH_LAYER_NAME_TO_SURFACE_INFO_MAP, onFetchLayerNameToSurfaceInfoMap),
        takeEvery(actions.FETCH_SOIL_SAMPLE_RESULTS, onFetchSoilSampleResults),
        takeEvery(actions.FETCH_TISSUE_SAMPLE_RESULTS, onFetchTissueSampleResults),
        takeEvery(actions.FETCH_MERGE_SOIL_SAMPLE_RESULTS, onFetchMergeSoilSampleResults),
        takeLatest(actions.FETCH_YIELD_CALIBRATION, onFetchYieldCalibration),
        takeLatest(actions.REFRESH_YIELD_CALIBRATION, onRefreshYieldCalibration),
        takeLatest(actions.RESTORE_BATCH_SAMPLE_POINTS, onRestoreBatchSamplePoints),
        takeLatest(actions.INITIALIZE_EVENTS_ZONES, onInitializeEventsZones),
        takeLatest(actions.INITIALIZE_EVENTS_ZONES_SUCCEEDED, onInitializeEventsZonesSucceeded),
        takeEvery(actions.VALIDATE_MERGE_EVENTS, onValidateEventsMerge),
        takeEvery(actions.VALIDATE_MERGE_EVENTS_SUCCEEDED, onValidateEventsMergeSucceeded),
        takeEvery(actions.VALIDATE_MERGE_EVENTS_FAILED, onMergeEventsFailed),
        takeEvery(actions.MERGE_EVENTS_SAVE, onMergeEventsSave),
        takeEvery(actions.MERGE_EVENTS_FAILED, onMergeEventsFailed),
        takeEvery(actions.RESET_CURRENT_EVENT_AREA_EVENT, onResetCurrentAgEventAreaAgEvent),
        takeEvery(
            [actions.RESET_EVENT_AREA_POLYGONS, actions.RESET_CLASSIFIED_EVENT_AREA_POLYGONS],
            onResetEventAreaPolygons
        ),
        takeEvery(actions.RESET_EVENT_AREAS, onResetEventAreas),
        takeEvery(actions.S3_PHOTO_COPY_FAILED, onS3PhotoCopyFailed),
        takeEvery(actions.S3_PHOTO_UPLOAD_FAILED, onS3PhotoUploadFailed),
        takeLatest(actions.SAVE_EVENT_DETAILS, onSaveEventDetails),
        takeEvery(actions.SET_BATCH_COPY_SAMPLE_POINTS, onSetBatchCopySamplePoints),
        takeEvery(actions.SET_EVENT_SAMPLE_POINTS, onSetEventSamplePoints),
        takeEvery(actions.SET_EVENT_ZONES_FROM_LAYER, onSetEventZonesFromLayer),
        takeEvery(actions.SET_MANUAL_ZONES, onSetManualZones),
        takeEvery(actions.SET_SAMPLING_EVENT_ID, onSetSamplingEventId),
        takeEvery(loginActions.SET_USER_INFO_COMPLETE, fetchEventTypeInfo),
        takeEvery(actions.UPDATE_BATCH_DEPTH_CONFIG, onUpdateBatchDepthConfig),
        takeEvery(actions.UPDATE_BATCH_SAMPLING_EVENTS, onUpdateBatchSamplingAgEvents),
        takeEvery(
            actions.UPDATE_CURRENT_EVENT_AREA_EVENT_MODEL,
            onUpdateCurrentAgEventAreaAgEventModel
        ),
        takeEvery(actions.UPDATE_EVENT_MODEL, onUpdateEventModel),
        takeEvery(actions.UPDATE_SAMPLING_EVENT_MODEL, onUpdateSamplingAgEventModel),
        takeEvery(actions.UPDATE_PLANTING_EVENT_MODEL, onUpdatePlantingEventModel),
        takeEvery(actions.UPDATE_EVENT_SUMMARY_IMPORTED_STATUS, onUpdateEventSummaryImportedStatus),
        takeEvery(actions.BATCH_EDIT_EVENTS_SAVE, onBatchEditEventsSave),
    ]);
};
